import { fileHref } from '../lib/fileAccess'
import { useState, useEffect, useRef } from 'react'
import { canRecord, startRecording, transcribe } from '../lib/voice'
import Workspaces from './Workspaces'
import { supabase } from '../lib/supabase'
import { deleteWithUndo } from '../lib/undoDelete'
import { useAuth } from '../hooks/useAuth'
import { fetchRows } from '../lib/api'
import html2pdf from 'html2pdf.js'
import { DRILL_TYPES } from '../lib/drillTypes'
import { filePath } from '../lib/filePaths'
import { shipNowAuto } from '../lib/shipTime'
import { isBriefedItem, parseBriefed, addBriefed, removeBriefed } from '../lib/briefed'
import { getShipTz } from '../lib/shipTime'
import EmergencyRun from '../components/EmergencyRun'
import { whoOf } from '../components/EmergencyLauncher'
import { CONTINGENCY_CATEGORY, LIBRARY_SOURCE, START_KEY, isContingency, sortPlans, stepCount, recordingOf, elapsedMs, fmtElapsed, CONTINGENCY_LIBRARY } from '../lib/contingency'
import { createPortal } from 'react-dom'
import LibraryPicker, { LibraryTag } from '../components/LibraryPicker'
import { planAdd, toWrite } from '../lib/libraryAdd'
import { runFor, startEmergency, subscribeEmergency } from '../lib/emergencyStore'
import LinkedManuals from '../components/LinkedManuals'
import CompanyStandardNote from '../components/CompanyStandardNote'
import { DRILL_START_KEY, resolveDrill, lastDrilled, lastDrilledLabel, drillTypesOf } from '../lib/drillPlans'

function getVal(v) {
  if (!v) return ''
  if (typeof v === 'object' && v.value) return v.value
  return String(v)
}

function sigCanvas(el) {
  if (!el) return
  el.width = el.offsetWidth || 260
  el.height = 90
  const ctx = el.getContext('2d')
  ctx.strokeStyle = '#e8e8e8'; ctx.lineWidth = 1.6; ctx.lineCap = 'round'
  let drawing = false
  el.onmousedown = e => { drawing = true; ctx.beginPath(); ctx.moveTo(e.offsetX, e.offsetY) }
  el.onmousemove = e => { if (!drawing) return; ctx.lineTo(e.offsetX, e.offsetY); ctx.stroke() }
  el.onmouseup = () => { drawing = false }
  el.onmouseleave = () => { drawing = false }
  el.ontouchstart = e => { e.preventDefault(); drawing = true; const t = e.touches[0]; const r = el.getBoundingClientRect(); ctx.beginPath(); ctx.moveTo(t.clientX-r.left, t.clientY-r.top) }
  el.ontouchmove = e => { e.preventDefault(); if (!drawing) return; const t = e.touches[0]; const r = el.getBoundingClientRect(); ctx.lineTo(t.clientX-r.left, t.clientY-r.top); ctx.stroke() }
  el.ontouchend = () => { drawing = false }
}
function clearSigCanvas(id) {
  const c = document.getElementById(id); if (c) c.getContext('2d').clearRect(0,0,c.width,c.height)
}

// Section grouping. category field wins; if absent, guess from the title so the
// page works before anything is tagged. Survives renaming once category is set.
const CATEGORY_ORDER = [
  'Permits to Work', 'Familiarisation', 'Handovers',
  'Pre-Departure / Arrival', 'Reports & Records',
  'Inspections & Safety Rounds', 'Regulatory / Compliance', 'Other',
]
// the sections a checklist can be put in (the edit / new forms): the routine ones + Contingency Plans (emergency checklists,
// their own tab, run in the emergency mode: src/components/EmergencyRun.jsx)
const SECTION_OPTIONS = [...CATEGORY_ORDER, CONTINGENCY_CATEGORY]
function cleanDesc(d) {
  if (!d) return d
  // strip a leading "MCM SMS-<codes>" reference in any format, with or without a dash/colon separator
  const x = String(d)
    .replace(/^\s*MCM\s+SMS[\s-]*[\d.\/-]*\s*[\u2014\u2013:-]?\s*/i, '')
    .trim()
  return x ? x.charAt(0).toUpperCase() + x.slice(1) : x
}

function categorise(c) {
  if (c.category) return c.category
  const t = (c.title || '').toLowerCase()
  if (/permit to work|permit-to-work|enclosed space/.test(t)) return 'Permits to Work'
  if (/familiar|bridge fam|navigation checklist/.test(t)) return 'Familiarisation'
  if (/handover/.test(t)) return 'Handovers'
  if (/pre-departure|pre departure|pre-arrival|pre arrival|cooling system|sms-29|sms-30/.test(t)) return 'Pre-Departure / Arrival'
  if (/report|meeting minutes|note of protest|defect|incident|drill report|monthly/.test(t)) return 'Reports & Records'
  if (/inspection|readiness|rounds|marpol|food & water|food and water|accommodation/.test(t)) return 'Inspections & Safety Rounds'
  if (/comply with|msn |mgn |guidance/.test(t)) return 'Regulatory / Compliance'
  return 'Other'
}

export default function Checklists({ onNavigate }) {
  const [tripCtx, setTripCtx] = useState(null) // set when opened from a Trip
  const [chkSearch, setChkSearch] = useState('')
  const { config, can, user } = useAuth()
  // a run in progress (or one just started from the Emergency button / the assistant / a risk assessment) opens the emergency mode
  const [emShow, setEmShow] = useState(() => !!runFor(config?.id))
  const [tab, setTab] = useState(() => (runFor(config?.id) ? 'contingency' : 'checklists'))
  const canEdit = !can || can('checklists', 'write')   // editing the templates needs Checklists Edit; running a plan only View
  const [emRun, setEmRun] = useState(() => runFor(config?.id))
  const [emRecords, setEmRecords] = useState(null)      // emergency_records of the vessel (null = not read / no table)
  const [openRecord, setOpenRecord] = useState(null)
  const [library, setLibrary] = useState([])           // the shared Hey Captain library (sql/contingency-library.sql); [] before it is run
  const [libOpen, setLibOpen] = useState(false)       // the shared "Adopt from Hey Captain library" picker
  const [drillInfo, setDrillInfo] = useState({ links: [], runs: [] })   // drill links + drill runs (last drilled per plan, sql/drills-contingency.sql)
  useEffect(() => {
    let seen = (runFor(config?.id) || {}).run?.id || null
    const show = () => { setEmShow(true); setTab('contingency') }
    window.addEventListener('hc-emergency-show', show)
    const off = subscribeEmergency(st => { const r = runFor(config?.id); setEmRun(r); if (r && r.run.id !== seen) { seen = r.run.id; show() } })
    return () => { window.removeEventListener('hc-emergency-show', show); off() }
  }, [config?.id])
  useEffect(() => { if (tab === 'contingency') { loadRecords(); loadLibrary().catch(() => setLibrary([])); loadDrillInfo() } }, [tab]) // eslint-disable-line
  async function loadDrillInfo() {
    const [l, r] = await Promise.all([
      supabase.from('drill_plan_links').select('drill_type,checklist_id').eq('client_id', config.id),
      supabase.from('checklist_instances').select('checklist_id,completed_at').eq('client_id', config.id).eq('drill', true).limit(5000),
    ])
    const info = { links: l.error ? [] : (l.data || []), runs: r.error ? [] : (r.data || []) }
    setDrillInfo(info)
    return info
  }
  const [checklists, setChecklists] = useState([])
  const [openSections, setOpenSections] = useState({})
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [showAI, setShowAI] = useState(false)
  const [activeChecklist, setActiveChecklist] = useState(null)
  useEffect(() => {
    if (!activeChecklist) return
    try {
      const pre = localStorage.getItem('hc_drill_prefill')
      if (pre) {
        localStorage.removeItem('hc_drill_prefill')
        const di = (activeChecklist.checklist_items || []).findIndex(it => it.is_drill_type)
        if (di >= 0) setStepNotes(prev => ({ ...prev, [di]: pre }))
      }
    } catch (e) {}
  }, [activeChecklist])
  const [parentRun, setParentRun] = useState(null)
  const [expandedGroups, setExpandedGroups] = useState({})
  const [viewHistory, setViewHistory] = useState(null)
  const [history, setHistory] = useState([])
  const [showGlobal, setShowGlobal] = useState(false)
  const [globalHistory, setGlobalHistory] = useState([])
  const [globalExpanded, setGlobalExpanded] = useState({})
  const [editChecklist, setEditChecklist] = useState(null)
  const [editTitle, setEditTitle] = useState('')
  const [editDesc, setEditDesc] = useState('')
  const [editSteps, setEditSteps] = useState([])
  const [editSaving, setEditSaving] = useState(false)
  const [editError, setEditError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('Other')
  const [editCategory, setEditCategory] = useState('Other')
  const [steps, setSteps] = useState([''])
  const [error, setError] = useState(null)
  const [checked, setChecked] = useState({})
  const [stepNotes, setStepNotes] = useState({})
  const [generalNote, setGeneralNote] = useState('')
  const [completing, setCompleting] = useState(false)
  const [completed, setCompleted] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [sigImages, setSigImages] = useState({})
  const [printMode, setPrintMode] = useState(false)
  const [voiceMode, setVoiceMode] = useState(false)
  const [voiceListening, setVoiceListening] = useState(false)
  const [voiceStatus, setVoiceStatus] = useState('')
  const voiceRec = useRef(null)   // { ctx, handle } while phone Hands-free records
  useEffect(() => { if (!voiceMode || !activeChecklist) stopRecordingMode() }, [voiceMode, activeChecklist]) // eslint-disable-line
  useEffect(() => () => stopRecordingMode(), []) // eslint-disable-line
  const [currentStep, setCurrentStep] = useState(0)
  const [standby, setStandby] = useState([])
  const [aiPrompt, setAiPrompt] = useState('')
  const [listening, setListening] = useState(false)
  const [aiLoading, setAiLoading] = useState(false)
  const [aiError, setAiError] = useState(null)
  const [aiDraft, setAiDraft] = useState(null)
  const [draftTitle, setDraftTitle] = useState('')
  const [draftDesc, setDraftDesc] = useState('')
  const [draftSteps, setDraftSteps] = useState([])
  const [crewList, setCrewList] = useState([])
  const [extraAttendee, setExtraAttendee] = useState({})
  const [showOffCrew, setShowOffCrew] = useState({})


  useEffect(() => { fetchChecklists() }, [])

  async function fetchChecklists() {
    setLoading(true)
    const { count: ownCount } = await supabase
      .from('checklists').select('id', { count: 'exact', head: true })
      .eq('client_id', config.id)
    if (!ownCount) {
      try { await supabase.rpc('clone_templates_to_vessel', { target: config.id }) } catch (e) { console.warn('clone failed', e) }
    }
    let { data, error } = await supabase
      .from('checklists')
      .select('*, checklist_items(*)')
      .eq('client_id', config.id)
      .order('created_at', { ascending: false })
    if (!error) {
      const _vn = config?.vessel_name || 'the vessel'
      const _sub = (t) => (typeof t === 'string' ? t.replace(/\{\{\s*vessel\s*\}\}/gi, _vn) : t)
      const mapped = data.map(c => ({
        ...c,
        title: _sub(c.title),
        description: _sub(c.description),
        checklist_items: [...(c.checklist_items || [])].sort((a, b) => a.step_order - b.step_order).map(it => ({ ...it, title: _sub(it.title) }))
      }))
      setChecklists(mapped)
      // "start this plan" from the assistant (open_contingency_plan) or a risk assessment's emergency arrangements
      try {
        const want = JSON.parse(localStorage.getItem(START_KEY) || 'null'); localStorage.removeItem(START_KEY)
        const plan = want && (mapped.find(c => c.id === want.id) || mapped.find(c => isContingency(c) && String(c.title).toLowerCase() === String(want.title || '').toLowerCase()))
        if (plan) startPlan(plan)
      } catch (e) {}
      // "Run drill" (Needs you, Drill Matrix, drills report, the assistant's card): the linked plan in drill mode; no linked plan
      // = the drill report checklist as before (src/lib/drillPlans.js resolveDrill)
      try {
        const dq = JSON.parse(localStorage.getItem(DRILL_START_KEY) || 'null'); localStorage.removeItem(DRILL_START_KEY)
        if (dq) {
          const { links } = await loadDrillInfo()
          const r = resolveDrill(dq, links, mapped.filter(isContingency))
          if (r && r.plan) startPlan(r.plan, 'drill', r.drillType)
          else if (r && r.fallback) { localStorage.setItem('hc_open_checklist', 'RR-04 Drill Report'); localStorage.setItem('hc_drill_prefill', r.fallback) }
        }
      } catch (e) {}
      const open = localStorage.getItem('hc_open_checklist')
      if (open) {
        localStorage.removeItem('hc_open_checklist')
        try { const tc = JSON.parse(localStorage.getItem('hc_trip_ctx') || 'null'); localStorage.removeItem('hc_trip_ctx'); if (tc && tc.id) setTripCtx(tc) } catch (e) {}
        const norm = t => String(t || '').toLowerCase().replace(/[^a-z0-9]/g, '')
        const want = norm(open)
        // a checklist id (a manual's link, an RA's pre-job checklist) or a title
        let found = mapped.find(c => String(c.id) === open) || mapped.find(c => c.title === open)
          || mapped.find(c => String(c.title || '').toLowerCase() === String(open).toLowerCase())
          || mapped.find(c => norm(c.title) === want)
        if (!found && want.length >= 6) {
          found = mapped.find(c => norm(c.title).includes(want) || want.includes(norm(c.title)))
        }
        if (found && isContingency(found)) startPlan(found)
        else if (found) setActiveChecklist(found)
        else console.warn('[hc_open_checklist] no checklist matched:', open)
      }
    }
    setLoading(false)
  }

  // start a Contingency Plan in the emergency mode (a run of the same plan already open on this device is resumed)
  // mode 'drill' + drillType: the plan in drill mode (src/lib/drillPlans.js); the same plan open in the other mode is not resumed
  function startPlan(plan, mode = 'emergency', drillType = null) {
    const cur = runFor(config?.id)
    if (!cur || cur.run.checklistId !== plan.id || (cur.run.mode || 'emergency') !== mode) {
      if (cur && !window.confirm('"' + cur.run.title + '" is still running' + (cur.run.mode === 'drill' ? ' (a drill)' : '') + '. Start "' + plan.title + '" instead? The open run is discarded - nothing of it is saved.')) { setEmShow(true); setTab('contingency'); return }
      startEmergency({ checklist: plan, who: whoOf(config), uid: user && user.id, vesselId: config.id, tz: getShipTz() || config.timezone || null, mode, drillType })
    }
    setEmShow(true); setTab('contingency')
  }
  // the library for the shared picker (CONTINGENCY_LIBRARY): plans with no vessel, with their steps (the picker's Preview)
  async function loadLibrary() {
    const { data, error } = await supabase.from('checklists').select('id,title,description,category,source,client_id,checklist_items(id,title,step_order,is_header,is_note,is_attendance,is_signature,is_drill_type)')
      .is('client_id', null).eq('source', LIBRARY_SOURCE).eq('category', CONTINGENCY_CATEGORY)
    if (error) throw new Error(error.message)
    const list = sortPlans((data || []).map(c => ({ ...c, checklist_items: [...(c.checklist_items || [])].sort((a, b) => a.step_order - b.step_order) })))
    setLibrary(list)
    return list
  }
  async function loadRecords() {
    const { data, error } = await supabase.from('emergency_records').select('*').eq('client_id', config.id).order('created_at', { ascending: false }).limit(200)
    setEmRecords(error ? null : (data || []))
  }
  async function saveRecord(rec, patch) {
    const { data, error } = await supabase.from('emergency_records').update(patch).eq('id', rec.id).select().maybeSingle()
    if (error) { alert('Not saved: ' + error.message); return }
    if (data) setOpenRecord(data)
    loadRecords()
  }

  async function openGlobalHistory() {
    setShowGlobal(true)
    const { data } = await supabase
      .from('checklist_instances')
      .select('*, checklists(title)')
      .eq('client_id', config.id)
      .order('completed_at', { ascending: false })
    setGlobalHistory(data || [])
  }

  async function openHistory(checklist) {
    setViewHistory(checklist)
    const { data } = await supabase
      .from('checklist_instances')
      .select('*')
      .eq('checklist_id', checklist.id)
      .eq('client_id', config.id)
      .order('started_at', { ascending: false })
    setHistory(data || [])
  }

  function openRunner(checklist, continueFrom = null) {
    setActiveChecklist(checklist)
    setChecked(continueFrom ? { ...(continueFrom.step_completion || {}) } : {})
    setStepNotes(continueFrom ? { ...(continueFrom.step_notes || {}) } : {})
    setGeneralNote('')
    setParentRun(continueFrom || null)
    setCompleted(false)
    setVoiceMode(false)
    setVoiceListening(false)
    setVoiceStatus('')
    setCurrentStep(0)
    setStandby([])
    if ((checklist.checklist_items || []).some(it => it.is_attendance)) {
      fetchRows('crew').then(rows => setCrewList(rows || [])).catch(() => setCrewList([]))
    } else {
      setCrewList([])
    }
  }

  // a checklist opened from elsewhere (the RA page's "Start pre-job checklist") skips startChecklist: the crew for its attendance / sign-on step is read here
  useEffect(() => {
    if (!activeChecklist || crewList.length || !(activeChecklist.checklist_items || []).some(it => it.is_attendance)) return
    fetchRows('crew').then(rows => setCrewList(rows || [])).catch(() => {})
  }, [activeChecklist]) // eslint-disable-line

  function speak(text) {
    window.speechSynthesis.cancel()
    const u = new SpeechSynthesisUtterance(text)
    u.lang = 'en-US'
    u.rate = 0.95
    window.speechSynthesis.speak(u)
  }

  function parseCommand(transcript) {
    const t = transcript.toLowerCase().trim()
    if (t.includes('next') || t.includes('next one') || t.includes('move on') || t.includes('proceed') || t.includes('continue')) return 'next'
    if (t.includes('done') || t.includes('complete') || t.includes('finished') || t.includes('check') || t.includes('yes') || t.includes('ok') || t.includes('confirmed') || t.includes('clear') || t.includes('tick')) return 'done'
    if (t.includes('stand by') || t.includes('standby') || t.includes('later') || t.includes('skip') || t.includes('not done') || t.includes('hold') || t.includes('pause') || t.includes('pending') || t.includes('wait')) return 'standby'
    if (t.includes('back') || t.includes('previous') || t.includes('go back') || t.includes('last one') || t.includes('before') || t.includes('prev')) return 'back'
    if (t.includes('what') || t.includes('remaining') || t.includes('list')) return 'pending'
    if (t.includes('finish') || t.includes('complete run') || t.includes('end') || t.includes('all done') || t.includes('that') || t.includes('wrap')) return 'finish'
    if (t.includes('repeat') || t.includes('again') || t.includes('say again') || t.includes('come again') || t.includes('what was')) return 'repeat'
    return 'unknown'
  }

  function startVoiceRunner(checklist, opts = {}) {
    stopRecordingMode()
    if (opts.record && canRecord()) {
      // created inside the tap so iPhone lets it run; used to hear when the speaker stops
      try { const AC = window.AudioContext || window.webkitAudioContext; const ctx = new AC(); ctx.resume && ctx.resume(); voiceRec.current = { ctx } } catch (e) { voiceRec.current = null }
    }
    setVoiceMode(true)
    setCurrentStep(0)
    setStandby([])
    setTimeout(() => {
      const items = checklist.checklist_items
      speak('Step 1. ' + items[0].title)
      setVoiceStatus('Step 1 of ' + items.length)
      setTimeout(() => listenForCommand(checklist, 0, {}, []), 3000)
    }, 500)
  }

  function handleCommand(checklist, step, currentChecked, currentStandby, transcript) {
    setVoiceStatus('Heard: ' + transcript)
    const cmd = parseCommand(transcript)
    const items = checklist.checklist_items
    const total = items.length
    const autoListen = (s, c, sb) => setTimeout(() => listenForCommand(checklist, s, c, sb), 2500)

    if (cmd === 'next') {
      const nextStep = step + 1
      if (nextStep >= total) {
        speak('Last step. Say finish to complete.')
        setVoiceStatus('Last step. Say finish.')
        autoListen(step, currentChecked, currentStandby)
      } else {
        setCurrentStep(nextStep)
        speak('Step ' + (nextStep + 1) + '. ' + items[nextStep].title)
        setVoiceStatus('Step ' + (nextStep + 1) + ' of ' + total)
        autoListen(nextStep, currentChecked, currentStandby)
      }
    } else if (cmd === 'done') {
      const newChecked = { ...currentChecked, [step]: true }
      setChecked(newChecked)
      const nextStep = step + 1
      if (nextStep >= total) {
        speak('All steps complete. Say finish to complete the run.')
        setVoiceStatus('All steps done. Say finish or pending.')
        setCurrentStep(nextStep)
        autoListen(nextStep, newChecked, currentStandby)
      } else {
        setCurrentStep(nextStep)
        speak('Step ' + (nextStep + 1) + '. ' + items[nextStep].title)
        setVoiceStatus('Step ' + (nextStep + 1) + ' of ' + total)
        autoListen(nextStep, newChecked, currentStandby)
      }
    } else if (cmd === 'standby') {
      const newStandby = [...currentStandby, step]
      setStandby(newStandby)
      const nextStep = step + 1
      if (nextStep >= total) {
        speak('Stand by noted. Say finish or pending.')
        setCurrentStep(nextStep)
        autoListen(nextStep, currentChecked, newStandby)
      } else {
        setCurrentStep(nextStep)
        speak('Stand by. Step ' + (nextStep + 1) + '. ' + items[nextStep].title)
        setVoiceStatus('Step ' + (nextStep + 1) + ' of ' + total)
        autoListen(nextStep, currentChecked, newStandby)
      }
    } else if (cmd === 'back') {
      const prevStep = Math.max(0, step - 1)
      setCurrentStep(prevStep)
      speak('Step ' + (prevStep + 1) + '. ' + items[prevStep].title)
      setVoiceStatus('Step ' + (prevStep + 1) + ' of ' + total)
      autoListen(prevStep, currentChecked, currentStandby)
    } else if (cmd === 'pending') {
      if (currentStandby.length === 0) {
        speak('No stand by items.')
      } else {
        const pendingText = currentStandby.map(i => 'Step ' + (i+1) + ', ' + items[i].title).join('. ')
        speak('Stand by items: ' + pendingText)
      }
      autoListen(step, currentChecked, currentStandby)
    } else if (cmd === 'finish') {
      speak('Run complete. Well done.')
      setVoiceMode(false)
      completeRun(currentChecked)
    } else if (cmd === 'repeat') {
      if (step < total) speak('Step ' + (step + 1) + '. ' + items[step].title)
      autoListen(step, currentChecked, currentStandby)
    } else {
      speak('Say done, stand by, go back, pending, repeat, or finish.')
      setVoiceStatus('Did not understand. Listening again...')
      autoListen(step, currentChecked, currentStandby)
    }
  }

  // Phone Hands-free: short recordings -> /api/ai?transcribe=1. Falls back to the phone's speech recognition if that fails.
  async function listenByRecording(checklist, step, currentChecked, currentStandby, tries = 0) {
    const v = voiceRec.current
    if (!v) return
    // wait until the step has been read out, so we don't record our own voice
    for (let i = 0; i < 150 && window.speechSynthesis && window.speechSynthesis.speaking; i++) await new Promise(r => setTimeout(r, 100))
    if (voiceRec.current !== v) return
    setVoiceListening(true)
    let text = ''
    try {
      const h = await startRecording({ maxMs: 10000, silenceMs: 900, noSpeechMs: 8000, audioCtx: v.ctx })
      v.handle = h
      const blob = await h.done
      if (voiceRec.current !== v) return
      setVoiceListening(false)
      if (!h.heard()) {
        if (tries < 2) return listenByRecording(checklist, step, currentChecked, currentStandby, tries + 1)
        setVoiceStatus('Did not hear anything. Tap Speak when ready.')
        return
      }
      setVoiceStatus('Working\u2026')
      text = await transcribe(blob, 'command')
    } catch (e) {
      setVoiceListening(false)
      if (voiceRec.current !== v) return
      stopRecordingMode()
      if (!(window.SpeechRecognition || window.webkitSpeechRecognition)) { setVoiceStatus('Voice not available: ' + e.message); return }
      setVoiceStatus('Using the phone\u2019s speech recognition\u2026')
      return listenForCommand(checklist, step, currentChecked, currentStandby)
    }
    if (voiceRec.current !== v) return
    if (!text) { setVoiceStatus('Did not catch that. Tap Speak to try again.'); return }
    handleCommand(checklist, step, currentChecked, currentStandby, text)
  }

  function stopRecordingMode() {
    const v = voiceRec.current
    voiceRec.current = null
    if (!v) return
    try { if (v.handle) v.handle.stop() } catch (e) {}
    try { v.ctx && v.ctx.close() } catch (e) {}
  }

  function listenForCommand(checklist, step, currentChecked, currentStandby) {
    if (voiceRec.current) return listenByRecording(checklist, step, currentChecked, currentStandby)
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SpeechRecognition) { speak('Voice not supported'); return }
    const recognition = new SpeechRecognition()
    recognition.lang = 'en-US'
    recognition.interimResults = false
    recognition.maxAlternatives = 3
    setVoiceListening(true)
    recognition.onresult = e => {
      setVoiceListening(false)
      handleCommand(checklist, step, currentChecked, currentStandby, e.results[0][0].transcript)
    }
    recognition.onerror = () => {
      setVoiceListening(false)
      setVoiceStatus('Did not catch that. Tap mic to try again.')
    }
    recognition.onend = () => setVoiceListening(false)
    recognition.start()
  }

  function toggleStep(i) {
    setChecked(prev => ({ ...prev, [i]: !prev[i] }))
  }

  async function completeRun(finalChecked, pdfUrl) {
    setCompleting(true)
    const items = activeChecklist.checklist_items
    const stepCompletion = {}
    const stepNotesMap = {}
    const checkState = (finalChecked && typeof finalChecked === 'object' && !finalChecked.nativeEvent) ? finalChecked : checked
    items.forEach((it, i) => {
      stepCompletion[i] = !!checkState[i]
      if (stepNotes[i]) stepNotesMap[i] = stepNotes[i]
      if (it.is_signature) {
        const c = document.getElementById('chk-sig-' + i)
        try { if (c) { const d = c.toDataURL('image/png'); if (d) stepNotesMap[i] = d } } catch (e) {}
      }
    })
    // Compute run label: fresh run = next integer; continuation = parent base + .N
    let runLabel = '1'
    let parentId = null
    try {
      const { data: prior } = await supabase
        .from('checklist_instances')
        .select('id, run_label, parent_run_id')
        .eq('checklist_id', activeChecklist.id)
        .eq('client_id', config.id)
      const rows = prior || []
      if (parentRun) {
        // Root integer = parent's label before any decimal (e.g. "3" from "3" or "3.2")
        const root = String(parentRun.run_label || '').split('.')[0]
        // Parent chain points at the ROOT record (the integer run), so all subs share one parent_run_id
        const rootRow = rows.find(r => String(r.run_label || '') === root)
        parentId = rootRow ? rootRow.id : (parentRun.parent_run_id || parentRun.id)
        // Highest existing decimal under this root
        const sibs = rows.filter(r => {
          const lbl = String(r.run_label || '')
          return lbl.includes('.') && lbl.split('.')[0] === root
        })
        const maxDec = sibs.reduce((m, r) => Math.max(m, parseInt(String(r.run_label).split('.')[1] || '0', 10)), 0)
        runLabel = root + '.' + (maxDec + 1)
      } else {
        const roots = rows.filter(r => !String(r.run_label || '').includes('.'))
        const maxInt = roots.reduce((m, r) => Math.max(m, parseInt(r.run_label || '0', 10) || 0), 0)
        runLabel = String(maxInt + 1)
      }
    } catch (e) { /* fall back to defaults */ }

    const forTrip = tripCtx && tripCtx.id && String(activeChecklist.title || '').toLowerCase().replace(/[^a-z0-9]/g, '') === tripCtx.key
    const { error } = await supabase.from('checklist_instances').insert({
      ...(forTrip ? { trip_id: tripCtx.id, ...(tripCtx.leg ? { leg_id: tripCtx.leg } : {}) } : {}),
      checklist_id: activeChecklist.id,
      client_id: config.id,
      started_by: config?.vessel_name || 'crew',
      step_completion: stepCompletion,
      step_notes: stepNotesMap,
      general_notes: generalNote,
      run_label: runLabel,
      parent_run_id: parentId,
      pdf_url: pdfUrl || null,
      completed_at: new Date().toISOString()
    })
    if (!error && forTrip) { setTripCtx(null); try { alert('Saved and linked to trip ' + (tripCtx.label || '')) } catch (e) {} }

    // Drill records (drill-type items) and snags (check items NOT ticked with a note -> "Defects") are written by the database
    // for every saved run - this page, the AI and the chat card alike (sql/consistency.sql hc_checklist_run_after)

    setCompleting(false)
    if (!error) setCompleted(true)
  }

  async function signExportComplete() {
    setExporting(true)
    try {
      const sigs = {}
      activeChecklist.checklist_items.forEach((it, i) => {
        if (it.is_signature) {
          const c = document.getElementById('chk-sig-' + i)
          try { if (c) sigs[i] = c.toDataURL('image/png') } catch (e) {}
        }
      })
      setSigImages(sigs)
      setPrintMode(true)
      await new Promise(r => setTimeout(r, 350))
      let publicUrl = ''
      const el = document.getElementById('chk-print')
      const safeTitle = (activeChecklist.title || 'checklist').replace(/[^a-zA-Z0-9]/g, '_')
      if (el) {
        window.scrollTo(0, 0)
        const opt = { margin: 8, filename: safeTitle + '.pdf', image: { type: 'jpeg', quality: 0.98 }, html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff', scrollX: 0, scrollY: 0, windowWidth: 960, height: el.scrollHeight, windowHeight: el.scrollHeight }, jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' } }
        const blob = await html2pdf().set(opt).from(el).outputPdf('blob')
        const path = filePath('checklists', config?.id, safeTitle + '.pdf')
        const { error: upErr } = await supabase.storage.from('crew-docs').upload(path, blob, { contentType: 'application/pdf', upsert: false })
        if (!upErr) publicUrl = path   // private bucket: the record keeps the PATH, opened through a signed link
      }
      setPrintMode(false)
      await completeRun(undefined, publicUrl)
    } catch (e) {
      setPrintMode(false)
      await completeRun()
    }
    setExporting(false)
  }

  function addStep() { setSteps([...steps, '']) }
  function removeStep(i) { setSteps(steps.filter((_, idx) => idx !== i)) }
  function updateStep(i, val) { const s = [...steps]; s[i] = val; setSteps(s) }

  async function saveChecklist() {
    if (!title.trim()) return setError('Title is required')
    const validSteps = steps.filter(s => s.trim())
    if (validSteps.length === 0) return setError('Add at least one step')
    setSaving(true); setError(null)
    const { data: cl, error: clErr } = await supabase
      .from('checklists')
      .insert({ client_id: config.id, title: title.trim(), description: description.trim(), category, source: 'manual', created_by: config?.vessel_name || 'crew' })
      .select().single()
    if (clErr) { setError(clErr.message); setSaving(false); return }
    const { error: itemsErr } = await supabase.from('checklist_items').insert(
      validSteps.map((s, i) => ({ checklist_id: cl.id, step_order: i + 1, title: s.trim(), required: true }))
    )
    if (itemsErr) { setError(itemsErr.message); setSaving(false); return }
    setTitle(''); setDescription(''); setSteps([''])
    setShowForm(false); setSaving(false)
    fetchChecklists()
  }

  function startListening(setter) {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SpeechRecognition) return setAiError('Voice not supported in this browser')
    const recognition = new SpeechRecognition()
    recognition.lang = 'en-US'
    recognition.interimResults = false
    recognition.onstart = () => setListening(true)
    recognition.onend = () => setListening(false)
    recognition.onresult = e => setter(e.results[0][0].transcript)
    recognition.onerror = () => setListening(false)
    recognition.start()
  }

  async function generateWithAI() {
    if (!aiPrompt.trim()) return setAiError('Please describe what checklist you need')
    setAiLoading(true); setAiError(null)
    try {
      const res = await fetch('/api/generate-checklist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: aiPrompt, vessel_name: config?.vessel_name })
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Generation failed')
      setAiDraft(data)
      setDraftTitle(data.title)
      setDraftDesc(data.description || '')
      setDraftSteps((data.steps || []).map(st => typeof st === 'string' ? { text: st, type: 'check' } : { text: st.text || st.title || '', type: st.type || 'check' }))
    } catch (err) {
      setAiError(err.message)
    }
    setAiLoading(false)
  }

  async function saveAIDraft() {
    if (!draftTitle.trim()) return setAiError('Title is required')
    const validSteps = draftSteps
      .map(st => typeof st === 'string' ? { text: st, type: 'check' } : { text: (st.text || st.title || ''), type: st.type || 'check' })
      .filter(st => st.text.trim())
    if (validSteps.length === 0) return setAiError('Add at least one step')
    setAiLoading(true); setAiError(null)
    try {
      const { data: cl, error: clErr } = await supabase
        .from('checklists')
        .insert({ client_id: config.id, title: draftTitle.trim(), description: draftDesc.trim(), source: (aiDraft && (aiDraft.sources || []).length) ? 'manual-ai' : 'ai', created_by: config?.vessel_name || 'crew' })
        .select().single()
      if (clErr) throw new Error('Supabase error: ' + JSON.stringify(clErr))
      // drafted from the vessel's manuals (fixes-2): linked to them both ways (the Manuals box here, the manual's links)
      if (aiDraft && (aiDraft.sources || []).length) await supabase.from('manual_links').insert(aiDraft.sources.map(x => ({ manual_id: x.manual_id, client_id: config.id, kind: 'checklist', target_id: String(cl.id), title: draftTitle.trim().slice(0, 160), note: x.note || x.label || null })))
      await supabase.from('checklist_items').insert(
        validSteps.map((st, i) => ({ checklist_id: cl.id, step_order: i + 1, title: st.text.trim(), required: st.type === 'check', is_header: st.type === 'header', is_note: st.type === 'note', is_attendance: st.type === 'attendance', is_drill_type: st.type === 'drill_type' }))
      )
      setAiPrompt(''); setShowAI(false); setAiDraft(null)
      setDraftTitle(''); setDraftDesc(''); setDraftSteps([])
      fetchChecklists()
    } catch (err) {
      setAiError(err.message)
    }
    setAiLoading(false)
  }

  async function deleteChecklist(checklist) {
    if (!window.confirm('Delete "' + checklist.title + '"? You can undo it for 10 seconds.')) return
    // its items are deleted first (kept here for the Undo); its completed runs come back with it
    const { data: items } = await supabase.from('checklist_items').select('*').eq('checklist_id', checklist.id)
    await supabase.from('checklist_items').delete().eq('checklist_id', checklist.id)
    try {
      await deleteWithUndo(supabase, { table: 'checklists', id: checklist.id, label: '"' + checklist.title + '"', children: [{ table: 'checklist_instances', column: 'checklist_id' }],
        onUndone: async () => { if (items && items.length) await supabase.from('checklist_items').upsert(items, { onConflict: 'id', ignoreDuplicates: true }); fetchChecklists() } })
    } catch (e) { if (items && items.length) await supabase.from('checklist_items').upsert(items, { onConflict: 'id', ignoreDuplicates: true }); alert('Could not delete: ' + (e.message || e)) }
    fetchChecklists()
  }

  function openEdit(checklist) {
    setEditCategory(checklist.category || categorise(checklist))
    setEditChecklist(checklist)
    setEditTitle(checklist.title)
    setEditDesc(checklist.description || '')
    setEditSteps(checklist.checklist_items.map(i => ({ title: i.title, is_header: i.is_header || false, is_note: i.is_note || false, is_attendance: i.is_attendance || false, is_signature: i.is_signature || false, is_drill_type: i.is_drill_type || false })))
    setEditError(null)
  }

  async function saveEdit() {
    if (!editTitle.trim()) return setEditError('Title is required')
    const validSteps = editSteps.filter(s => s.title.trim())
    if (validSteps.length === 0) return setEditError('Add at least one step')
    setEditSaving(true); setEditError(null)
    try {
      const { error: titleErr } = await supabase
        .from('checklists')
        .update({ title: editTitle.trim(), description: editDesc.trim(), category: editCategory, updated_at: new Date().toISOString() })
        .eq('id', editChecklist.id)
      if (titleErr) throw new Error(titleErr.message)
      await supabase.from('checklist_items').delete().eq('checklist_id', editChecklist.id)
      await supabase.from('checklist_items').insert(
        validSteps.map((s, i) => ({ checklist_id: editChecklist.id, step_order: i + 1, title: s.title.trim(), required: !s.is_header && !s.is_note && !s.is_attendance && !s.is_signature, is_header: s.is_header, is_note: s.is_note || false, is_attendance: s.is_attendance || false, is_signature: s.is_signature || false, is_drill_type: s.is_drill_type || false }))
      )
      setEditChecklist(null)
      fetchChecklists()
    } catch (err) {
      setEditError(err.message)
    }
    setEditSaving(false)
  }

  const s = styles

  if (emShow) return (
    <div style={s.wrap}>
      <EmergencyRun config={config} who={whoOf(config)} canTodo={!can || can('todo', 'write')} onExit={() => { setEmShow(false); fetchChecklists(); loadRecords(); loadDrillInfo() }}
        formChecklists={checklists.filter(c => !isContingency(c))}
        onOpenForm={(f, text) => { setEmShow(false); openRunner(f); setGeneralNote(text) }} />
    </div>
  )

  if (editChecklist) {
    return (
      <div style={s.wrap}>
        <button style={s.backBtn} onClick={() => setEditChecklist(null)}>Back</button>
        <div style={s.form}>
          <h2 style={s.formTitle}>Edit checklist</h2>
          {editError && <p style={s.error}>{editError}</p>}
          <label style={s.label}>Title</label>
          <input style={s.input} value={editTitle} onChange={e => setEditTitle(e.target.value)} />
          <label style={s.label}>Description</label>
          <input style={s.input} value={editDesc} onChange={e => setEditDesc(e.target.value)} />
          <label style={s.label}>Section</label>
          <select style={s.input} value={editCategory} onChange={e => setEditCategory(e.target.value)}>
            {SECTION_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <label style={s.label}>Steps</label>
          {editSteps.map((step, i) => (
            <div key={i}
              draggable
              onDragStart={e => { e.dataTransfer.setData('text/plain', String(i)); e.dataTransfer.effectAllowed='move' }}
              onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect='move' }}
              onDrop={e => { e.preventDefault(); const from=parseInt(e.dataTransfer.getData('text/plain')); if(from===i) return; const ns=[...editSteps]; const [moved]=ns.splice(from,1); ns.splice(i,0,moved); setEditSteps(ns) }}
              style={{...s.stepRow, background: step.is_header ? 'rgba(184,150,62,.08)' : step.is_note ? 'rgba(106,170,212,.08)' : step.is_attendance ? 'rgba(93,186,142,.08)' : step.is_signature ? 'rgba(106,170,212,.05)' : 'transparent', borderRadius:6, padding:'2px 0', cursor:'grab'}}>
              <span style={{color:'var(--muted)', fontSize:16, padding:'0 4px', cursor:'grab', userSelect:'none'}}>⠿</span>
              {!step.is_header && !step.is_note && !step.is_attendance && !step.is_signature && <span style={s.stepNum}>{editSteps.slice(0,i).filter(x=>!x.is_header && !x.is_note && !x.is_attendance && !x.is_signature).length + 1}</span>}
              <input
                style={{...s.input, flex: 1, margin: 0, fontWeight: step.is_header ? 700 : 400, color: step.is_header ? 'var(--gold2)' : step.is_note ? '#2a6a96' : 'var(--text)'}}
                value={step.title}
                placeholder={step.is_header ? 'Section header...' : step.is_note ? 'Note box label (e.g. Additional information)...' : step.is_attendance ? 'Attendance label (e.g. Crew in attendance)...' : step.is_signature ? 'Signature label (e.g. Signature of Captain)...' : 'Step description...'}
                onChange={e => { const ns = [...editSteps]; ns[i] = {...ns[i], title: e.target.value}; setEditSteps(ns) }}
              />
              <button style={s.removeBtn} onClick={() => setEditSteps(editSteps.filter((_, idx) => idx !== i))}>x</button>
            </div>
          ))}
          <div style={{display:'flex', gap:6, flexWrap:'wrap', marginTop:6}}>
            <button style={s.addTypeBtn} onClick={() => setEditSteps([...editSteps, { title: '', is_header: false, is_note: false, is_attendance: false, is_signature: false }])}>+ Step</button>
            <button style={{...s.addTypeBtn, color:'var(--gold2)'}} onClick={() => setEditSteps([...editSteps, { title: '', is_header: true, is_note: false, is_attendance: false, is_signature: false }])}>+ Header</button>
            <button style={{...s.addTypeBtn, color:'#2a6a96'}} onClick={() => setEditSteps([...editSteps, { title: '', is_header: false, is_note: true, is_attendance: false, is_signature: false }])}>+ Note</button>
            <button style={{...s.addTypeBtn, color:'#137050'}} onClick={() => setEditSteps([...editSteps, { title: 'Crew in attendance', is_header: false, is_note: false, is_attendance: true, is_signature: false }])}>+ Attendance</button>
            <button style={{...s.addTypeBtn, color:'#2a6a96'}} onClick={() => setEditSteps([...editSteps, { title: 'Signature', is_header: false, is_note: false, is_attendance: false, is_signature: true }])}>+ Signature</button>
            <button style={{...s.addTypeBtn, color:'var(--gold2)'}} onClick={() => setEditSteps([...editSteps, { title: 'Drill type(s) covered by this report', is_header: false, is_note: false, is_attendance: false, is_signature: false, is_drill_type: true }])}>+ Drill Type</button>
          </div>
          <div style={s.formActions}>
            <button style={s.cancelBtn} onClick={() => setEditChecklist(null)}>Cancel</button>
            <button style={s.saveBtn} onClick={saveEdit} disabled={editSaving}>
              {editSaving ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (showGlobal) {
    const fmtPct = r => {
      const vals = Object.values(r.step_completion || {})
      const done = vals.filter(Boolean).length
      const total = vals.length || 1
      return Math.round((done/total)*100)
    }
    return (
      <div style={s.wrap}>
        <button style={s.backBtn} onClick={() => { setShowGlobal(false); setGlobalHistory([]) }}>Back</button>
        <h2 style={s.runnerTitle}>All checklist activity</h2>
        <p style={{...s.muted, marginBottom: 20}}>{globalHistory.length} run{globalHistory.length !== 1 ? 's' : ''} across all checklists</p>
        {globalHistory.length === 0 ? (
          <p style={s.muted}>No runs recorded yet.</p>
        ) : (
          <div style={s.list}>
            {globalHistory.map(run => {
              const pct = fmtPct(run)
              const date = new Date(run.completed_at || run.started_at)
              const name = run.checklists?.title || 'Checklist'
              const open = !!globalExpanded[run.id]
              return (
                <div key={run.id} style={s.card}>
                  <div style={{...s.cardTop, cursor:'pointer'}} onClick={() => setGlobalExpanded(p => ({...p, [run.id]: !p[run.id]}))}>
                    <span style={s.cardTitle}>{name}{run.run_label ? '  \u00b7  Run ' + run.run_label : ''}</span>
                    <span style={{...s.badge, background: pct === 100 ? 'rgba(93,186,142,.15)' : 'rgba(201,147,58,.15)', color: pct === 100 ? '#137050' : 'var(--gold2)'}}>{pct}% done</span>
                  </div>
                  <p style={s.cardMeta}>{date.toLocaleString()} by {run.started_by}</p>
                  {run.general_notes && <p style={s.cardDesc}>Note: {run.general_notes}</p>}
                  {open && (
                    <div style={{marginTop: 10}}>
                      {Object.keys(run.step_completion || {}).length === 0
                        ? <p style={s.muted}>No step detail recorded.</p>
                        : Object.entries(run.step_completion).map(([i, done]) => (
                          <div key={i} style={{display:'flex', gap:8, alignItems:'flex-start', marginBottom:4}}>
                            <span style={{color: done ? '#137050' : '#b4313c', fontSize:12, marginTop:1}}>{done ? 'v' : 'x'}</span>
                            <p style={{fontSize:12, color: done ? 'var(--text)' : 'var(--muted)', margin:0}}>Step {parseInt(i,10)+1}{run.step_notes?.[i] ? ' — ' + run.step_notes[i] : ''}</p>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  if (viewHistory) {
    const items = viewHistory.checklist_items || []
    return (
      <div style={s.wrap}>
        <button style={s.backBtn} onClick={() => { setViewHistory(null); setHistory([]) }}>Back</button>
        <h2 style={s.runnerTitle}>{viewHistory.title} — Run history</h2>
        <p style={{...s.muted, marginBottom: 20}}>{history.length} run{history.length !== 1 ? 's' : ''} recorded</p>
        {history.length === 0 ? (
          <p style={s.muted}>No runs yet. Tap the checklist to run it.</p>
        ) : (
          <div style={s.list}>
            {(() => {
              const total = items.length
              const rootKey = r => String(r.run_label || '').split('.')[0] || '0'
              const isSub = r => String(r.run_label || '').includes('.')
              const roots = history.filter(r => !isSub(r))
              const subsByRoot = {}
              history.filter(isSub).forEach(r => { const k = rootKey(r); (subsByRoot[k] = subsByRoot[k] || []).push(r) })
              roots.sort((a,b) => (parseInt(rootKey(b),10)||0) - (parseInt(rootKey(a),10)||0))

              const doneOf = r => Object.values(r.step_completion || {}).filter(Boolean).length
              const renderCard = (run, isSubCard) => {
                const done = doneOf(run)
                const pct = total > 0 ? Math.round((done/total)*100) : 0
                const date = new Date(run.completed_at || run.started_at)
                return (
                  <div key={run.id} style={{...s.card, ...(isSubCard ? { marginLeft: 28, borderLeft: '2px solid var(--border)' } : {})}}>
                    <div style={s.cardTop}>
                      <span style={s.cardTitle}>Run {run.run_label}</span>
                      <span style={{...s.badge, background: pct === 100 ? 'rgba(93,186,142,.15)' : 'rgba(201,147,58,.15)', color: pct === 100 ? '#137050' : 'var(--gold2)'}}>{pct}% done</span>
                    </div>
                    <p style={s.cardDesc}>{done} of {total} steps completed</p>
                    {run.general_notes && <p style={s.cardDesc}>Note: {run.general_notes}</p>}
                    <p style={s.cardMeta}>{date.toLocaleString()} by {run.started_by}</p>
                    {run.pdf_url && <a href={fileHref(run.pdf_url)} target="_blank" rel="noopener noreferrer" style={{display:'inline-flex', alignItems:'center', gap:6, marginTop:8, background:'rgba(184,150,62,.15)', border:'1px solid var(--gold2)', color:'var(--gold2)', borderRadius:8, padding:'7px 14px', fontSize:13, fontWeight:600, textDecoration:'none'}}>📄 View signed PDF</a>}
                    <div style={{marginTop: 10}}>
                      {items.map((item, i) => {
                        const isDone = run.step_completion?.[i]
                        const note = run.step_notes?.[i]
                        if (item.is_header) return (
                          <div key={i} style={{fontSize:11, fontWeight:700, color:'var(--gold2)', textTransform:'uppercase', letterSpacing:'.06em', margin:'8px 0 2px'}}>{item.title}</div>
                        )
                        if (item.is_signature) return (
                          <div key={i} style={{marginBottom:6}}>
                            <p style={{fontSize:11, fontWeight:600, color:'#2a6a96', margin:0}}>{item.title || 'Signature'}</p>
                            {note ? <img src={note} alt="signature" style={{height:48, background:'#0a1119', border:'1px solid var(--border)', borderRadius:4, marginTop:2}} /> : <p style={{fontSize:12, color:'var(--muted)', margin:0}}>Not signed</p>}
                          </div>
                        )
                        if (item.is_attendance) return (
                          <div key={i} style={{marginBottom:6}}>
                            <p style={{fontSize:11, fontWeight:600, color:'var(--gold2)', margin:0}}>{item.title || 'Crew in attendance'}</p>
                            <p style={{fontSize:12, color: note ? 'var(--text)' : 'var(--muted)', margin:0}}>{note || '—'}</p>
                          </div>
                        )
                        if (item.is_note) return (
                          <div key={i} style={{marginBottom:6}}>
                            <p style={{fontSize:11, fontWeight:600, color:'#2a6a96', margin:0}}>{item.title}</p>
                            <p style={{fontSize:12, color: note ? 'var(--text)' : 'var(--muted)', margin:0}}>{note || '—'}</p>
                          </div>
                        )
                        return (
                          <div key={i} style={{display:'flex', gap:8, alignItems:'flex-start', marginBottom:6}}>
                            <span style={{color: isDone ? '#137050' : '#b4313c', fontSize:12, marginTop:1}}>{isDone ? 'v' : 'x'}</span>
                            <div>
                              <p style={{fontSize:12, color: isDone ? 'var(--text)' : 'var(--muted)', margin:0, textDecoration: isDone ? 'line-through' : 'none', opacity: isDone ? 0.6 : 1}}>{item.title}</p>
                              {note && <p style={{fontSize:11, color:'var(--muted)', margin:0}}>Note: {note}</p>}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                    <button style={{...s.continueBtn}} onClick={() => { const cl = viewHistory; setViewHistory(null); openRunner(cl, run) }}>Continue this run</button>
                  </div>
                )
              }

              return roots.map(root => {
                const k = rootKey(root)
                const subs = (subsByRoot[k] || []).sort((a,b) =>
                  (parseInt(String(a.run_label).split('.')[1]||'0',10)) - (parseInt(String(b.run_label).split('.')[1]||'0',10)))
                // collapsed card reflects the LATEST (most complete) state in the chain
                const latest = subs.length ? subs.reduce((best, r) => doneOf(r) >= doneOf(best) ? r : best, subs[subs.length-1]) : root
                const isOpen = !!expandedGroups[k]
                return (
                  <div key={'g'+k}>
                    {renderCard(latest, false)}
                    {subs.length > 0 && (
                      <>
                        <button style={s.expander} onClick={() => setExpandedGroups(p => ({ ...p, [k]: !p[k] }))}>
                          <span style={{transform: isOpen ? 'rotate(90deg)' : 'none', display:'inline-block', transition:'transform .15s'}}>&#8250;</span>
                          {isOpen ? 'Hide history' : 'Show history (' + (subs.length + 1) + ' runs)'}
                        </button>
                        {isOpen && (
                          <div>
                            {renderCard(root, true)}
                            {subs.map(s2 => renderCard(s2, true))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )
              })
            })()}
          </div>
        )}
        <div style={{marginTop: 20}}>
          <button style={s.saveBtn} onClick={() => { setViewHistory(null); openRunner(viewHistory) }}>Run again</button>
        </div>
      </div>
    )
  }

  if (activeChecklist) {
    const items = activeChecklist.checklist_items
    const nonHeaders = items.filter(item => !item.is_header && !item.is_note && !item.is_attendance && !item.is_signature)
    const doneCount = nonHeaders.filter((_, i) => checked[items.indexOf(nonHeaders[i])]).length
    const allDone = nonHeaders.every((item, i) => checked[items.indexOf(item)])

    if (completed) return (
      <div style={s.wrap}>
        <div style={{...s.form, textAlign: 'center', padding: '2.5rem'}}>
          <div style={{fontSize: 48, marginBottom: 12}}>v</div>
          <h2 style={{color: 'var(--text)', fontWeight: 600, marginBottom: 8}}>{activeChecklist.title}</h2>
          <p style={s.muted}>Completed - {doneCount} of {items.length} steps done</p>
          <p style={{...s.muted, fontSize: 12, marginTop: 4}}>{new Date().toLocaleString()}</p>
          <div style={{display:'flex', gap:12, justifyContent:'center', marginTop:24}}>
            <button style={s.cancelBtn} onClick={() => openHistory(activeChecklist)}>View history</button>
            <button style={s.saveBtn} onClick={() => setActiveChecklist(null)}>Back to checklists</button>
          </div>
        </div>
      </div>
    )

    return (
      <div style={s.wrap}>
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem'}}>
          <button style={s.backBtn} onClick={() => { setActiveChecklist(null); window.speechSynthesis.cancel() }}>Back</button>
          <button className={voiceMode ? '' : 'hc-voice-small'}
            style={{background: voiceMode ? 'rgba(224,112,112,.15)' : 'rgba(93,186,142,.15)', border: voiceMode ? '1px solid #b4313c' : '1px solid #137050', borderRadius:8, padding:'8px 16px', cursor:'pointer', color: voiceMode ? '#b4313c' : '#137050', fontSize:13, fontWeight:600}}
            onClick={() => voiceMode ? (setVoiceMode(false), window.speechSynthesis.cancel()) : startVoiceRunner(activeChecklist)}
          >
            {voiceMode ? 'Stop voice' : 'Voice assistant'}
          </button>
        </div>
        {activeChecklist && activeChecklist.id && <CompanyStandardNote kind="checklist" localId={activeChecklist.id} />}
        {activeChecklist && activeChecklist.id && <LinkedRas checklistId={activeChecklist.id} clientId={config?.id} />}
        {activeChecklist && activeChecklist.id && <LinkedManuals kind="checklist" targetId={activeChecklist.id} title={activeChecklist.title || ''} />}
        <style>{`
          .hc-handsfree { display: none; }
          @media (max-width: 1023px), (display-mode: standalone) {
            .hc-handsfree { display: flex; }
            .hc-voice-small { display: none; }
          }
        `}</style>
        {!voiceMode && (
          <button className="hc-handsfree" onClick={() => startVoiceRunner(activeChecklist, { record: true })}
            style={{width:'100%', minHeight:56, marginBottom:16, alignItems:'center', justifyContent:'center', gap:10, background:'#137050', color:'#fff', border:'none', borderRadius:12, fontSize:17, fontWeight:700, cursor:'pointer'}}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 14v-2a9 9 0 0 1 18 0v2"/><rect x="2.5" y="14" width="5" height="7" rx="1.5"/><rect x="16.5" y="14" width="5" height="7" rx="1.5"/></svg>
            Hands-free
          </button>
        )}
        {voiceMode && (
          <div style={{background:'rgba(93,186,142,.08)', border:'1px solid rgba(93,186,142,.3)', borderRadius:10, padding:'1rem', marginBottom:16}}>
            <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:8}}>
              <div style={{width:10, height:10, borderRadius:'50%', background: voiceListening ? '#b4313c' : '#137050', animation: voiceListening ? 'pulse 1s infinite' : 'none'}} />
              <span style={{fontSize:13, fontWeight:600, color:'var(--text)'}}>
                {voiceListening ? 'Listening...' : 'Voice mode active'}
              </span>
              <button
                style={{marginLeft:'auto', background:'var(--gold)', color:'var(--navy)', border:'none', borderRadius:6, padding:'6px 14px', cursor:'pointer', fontSize:12, fontWeight:600}}
                onClick={() => listenForCommand(activeChecklist, currentStep, checked, standby)}
                disabled={voiceListening}
              >
                {voiceListening ? 'Listening...' : 'Speak'}
              </button>
            </div>
            {voiceStatus && <p style={{fontSize:12, color:'var(--muted)', margin:0}}>{voiceStatus}</p>}
            <p style={{fontSize:11, color:'var(--muted)', margin:'8px 0 0 0'}}>Commands: done · stand by · go back · pending · repeat · finish</p>
          </div>
        )}
        <div style={s.runnerHeader}>
          <h2 style={s.runnerTitle}>{activeChecklist.title}</h2>
          {activeChecklist.description && <p style={s.muted}>{cleanDesc(activeChecklist.description)}</p>}
          <div style={s.progressBar}>
            <div style={{...s.progressFill, width: doneCount === 0 ? '0%' : ((doneCount/items.length)*100)+'%'}} />
          </div>
          <p style={s.progressText}>{doneCount} of {nonHeaders.length} steps completed</p>
        </div>
        <div style={s.stepsList}>
          {items.map((item, i) => (
            item.is_header ? (
              <div key={item.id} style={{ padding:'10px 4px 4px', marginTop: i===0?0:12 }}>
                <div style={{ fontSize:11, fontWeight:700, color:'var(--gold2)', textTransform:'uppercase', letterSpacing:'.08em', borderBottom:'1px solid var(--border)', paddingBottom:6 }}>{item.title}</div>
              </div>
            ) : item.is_signature ? (
              <div key={item.id} style={{...s.stepCard, flexDirection:'column', alignItems:'stretch', gap:8}}>
                <p style={{...s.stepTitle, margin:0, color:'#2a6a96', fontWeight:600}}>{item.title || 'Signature'}</p>
                <div style={{position:'relative'}}>
                  <canvas ref={sigCanvas} id={'chk-sig-' + i} style={{width:'100%', height:90, background:'var(--ink)', border:'1px solid var(--border)', borderRadius:8, touchAction:'none'}} />
                  <button onClick={() => clearSigCanvas('chk-sig-' + i)} style={{position:'absolute', top:6, right:8, background:'transparent', border:'none', color:'var(--muted)', fontSize:12, cursor:'pointer'}}>Clear</button>
                </div>
              </div>
            ) : isBriefedItem(item) ? (
              <BriefedStep key={item.id} item={item} note={stepNotes[i] || ''} crewList={crewList} extra={extraAttendee[i] || ''}
                setExtra={v => setExtraAttendee(prev => ({ ...prev, [i]: v }))} setNote={v => setStepNotes(prev => ({ ...prev, [i]: v }))} />
            ) : item.is_attendance ? (
              <div key={item.id} style={{...s.stepCard, flexDirection:'column', alignItems:'stretch', gap:8}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', gap:8, flexWrap:'wrap'}}>
                  <p style={{...s.stepTitle, margin:0, color:'var(--gold2)', fontWeight:600}}>{item.title || 'Crew in attendance'}</p>
                  <button type="button" onClick={() => {
                    const onboardNames = crewList.filter(cw => { const ob = getVal(cw['Onboard']); return ob === true || ob === 'true' || ob === 'Yes' || ob === 1 }).map(cw => (getVal(cw['First Name']) + ' ' + getVal(cw['Last Name'])).trim()).filter(Boolean)
                    const cur = (stepNotes[i] || '').split(', ').filter(Boolean)
                    const merged = Array.from(new Set([...cur, ...onboardNames]))
                    setStepNotes(prev => ({...prev, [i]: merged.join(', ')}))
                  }} style={{border:'0.5px solid var(--gold2)', background:'rgba(201,147,58,.12)', color:'var(--gold2)', fontSize:11.5, fontWeight:600, padding:'5px 11px', borderRadius:6, cursor:'pointer'}}>Select all onboard</button>
                </div>
                <div style={{display:'flex', flexDirection:'column', gap:2}}>
                  {crewList.length === 0 ? <p style={s.muted}>Loading crew…</p> : crewList.filter(cw => { const ob = getVal(cw['Onboard']); return ob === true || ob === 'true' || ob === 'Yes' || ob === 1 }).map(cw => {
                    const nm = (getVal(cw['First Name']) + ' ' + getVal(cw['Last Name'])).trim()
                    if (!nm) return null
                    const sel = (stepNotes[i] || '').split(', ').filter(Boolean)
                    const on = sel.includes(nm)
                    return (
                      <label key={cw.id} style={{display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, color:'var(--text)', padding:'3px 0'}}>
                        <input type="checkbox" checked={on} onChange={() => {
                          const cur = (stepNotes[i] || '').split(', ').filter(Boolean)
                          const next = on ? cur.filter(x => x !== nm) : [...cur, nm]
                          setStepNotes(prev => ({...prev, [i]: next.join(', ')}))
                        }} />
                        {nm}
                      </label>
                    )
                  })}
                  {(stepNotes[i] || '').split(', ').filter(Boolean).filter(nm => !crewList.some(cw => (getVal(cw['First Name']) + ' ' + getVal(cw['Last Name'])).trim() === nm)).map(extra => (
                    <label key={'x-'+extra} style={{display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, color:'var(--text)', padding:'3px 0'}}>
                      <input type="checkbox" checked readOnly onChange={() => {
                        const cur = (stepNotes[i] || '').split(', ').filter(Boolean)
                        setStepNotes(prev => ({...prev, [i]: cur.filter(x => x !== extra).join(', ')}))
                      }} />
                      {extra} <span style={{fontSize:11, color:'var(--muted)'}}>(guest)</span>
                    </label>
                  ))}
                  {showOffCrew[i] && crewList.filter(cw => { const ob = getVal(cw['Onboard']); return !(ob === true || ob === 'true' || ob === 'Yes' || ob === 1) }).map(cw => {
                    const nm = (getVal(cw['First Name']) + ' ' + getVal(cw['Last Name'])).trim()
                    if (!nm) return null
                    const on = (stepNotes[i] || '').split(', ').filter(Boolean).includes(nm)
                    return (
                      <label key={'off-'+cw.id} style={{display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, color:'var(--muted)', padding:'3px 0'}}>
                        <input type="checkbox" checked={on} onChange={() => {
                          const cur = (stepNotes[i] || '').split(', ').filter(Boolean)
                          const next = on ? cur.filter(x => x !== nm) : [...cur, nm]
                          setStepNotes(prev => ({...prev, [i]: next.join(', ')}))
                        }} />
                        {nm} <span style={{fontSize:11, color:'var(--muted)'}}>(off)</span>
                      </label>
                    )
                  })}
                </div>
                <button type="button" onClick={() => setShowOffCrew(prev => ({...prev, [i]: !prev[i]}))} style={{alignSelf:'flex-start', background:'none', border:'none', color:'var(--gold2)', fontSize:11.5, cursor:'pointer', fontFamily:'inherit', padding:'2px 0'}}>{showOffCrew[i] ? '− Hide off-crew' : '+ Show off-crew'}</button>
                <div style={{display:'flex', gap:6, marginTop:4}}>
                  <input placeholder="Add other attendee (guest, contractor)..." value={extraAttendee[i] || ''} onChange={e => setExtraAttendee(prev => ({...prev, [i]: e.target.value}))} onKeyDown={e => { if (e.key === 'Enter') { const v = (extraAttendee[i]||'').trim(); if (v) { const cur = (stepNotes[i]||'').split(', ').filter(Boolean); if (!cur.includes(v)) setStepNotes(prev => ({...prev, [i]: [...cur, v].join(', ')})); setExtraAttendee(prev => ({...prev, [i]: ''})) } } }} style={{flex:1, background:'var(--bg, #0b0f14)', border:'0.5px solid var(--border)', borderRadius:6, padding:'6px 9px', color:'var(--text)', fontSize:12.5, fontFamily:'inherit'}} />
                  <button type="button" onClick={() => { const v = (extraAttendee[i]||'').trim(); if (v) { const cur = (stepNotes[i]||'').split(', ').filter(Boolean); if (!cur.includes(v)) setStepNotes(prev => ({...prev, [i]: [...cur, v].join(', ')})); setExtraAttendee(prev => ({...prev, [i]: ''})) } }} style={{border:'0.5px solid var(--gold2)', background:'rgba(201,147,58,.12)', color:'var(--gold2)', fontSize:12.5, fontWeight:600, padding:'6px 12px', borderRadius:6, cursor:'pointer'}}>Add</button>
                </div>
              </div>
            ) : item.is_drill_type ? (
              <div key={item.id} style={{...s.stepCard, flexDirection:'column', alignItems:'stretch', gap:8}}>
                <p style={{...s.stepTitle, margin:0, color:'var(--gold2)', fontWeight:600}}>{item.title || 'Drill type(s) covered'}</p>
                <div style={{display:'flex', flexWrap:'wrap', gap:6}}>
                  {DRILL_TYPES.map(dt => {
                    const sel = (stepNotes[i] || '').split(', ').filter(Boolean)
                    const on = sel.includes(dt.label)
                    return (
                      <button key={dt.key} type="button" onClick={() => {
                        const cur = (stepNotes[i] || '').split(', ').filter(Boolean)
                        const next = on ? cur.filter(x => x !== dt.label) : [...cur, dt.label]
                        setStepNotes(prev => ({...prev, [i]: next.join(', ')}))
                      }} style={{
                        fontSize:12, padding:'6px 12px', borderRadius:16, cursor:'pointer',
                        border: on ? '1px solid var(--gold)' : '1px solid var(--border)',
                        background: on ? 'rgba(184,150,62,.15)' : 'transparent',
                        color: on ? 'var(--gold2)' : 'var(--muted)', fontWeight: on ? 600 : 400
                      }}>{on ? '✓ ' : ''}{dt.label}</button>
                    )
                  })}
                </div>
              </div>
            ) : item.is_note ? (
              <div key={item.id} style={{...s.stepCard, flexDirection:'column', alignItems:'stretch', gap:8}}>
                <p style={{...s.stepTitle, margin:0, color:'#2a6a96', fontWeight:600}}>{item.title}</p>
                <textarea
                  style={{...s.input, minHeight:64, resize:'vertical'}}
                  placeholder="Enter notes..."
                  value={stepNotes[i] || ''}
                  onChange={e => setStepNotes(prev => ({...prev, [i]: e.target.value}))}
                />
              </div>
            ) : (
            <div key={item.id} style={{...s.stepCard, ...(checked[i] ? s.stepDone : {})}}>
              <button style={{...s.checkbox, ...(checked[i] ? s.checkboxDone : {})}} onClick={() => toggleStep(i)}>
                {checked[i] ? 'v' : ''}
              </button>
              <div style={{flex: 1}}>
                <p style={{...s.stepTitle, ...(checked[i] ? {textDecoration: 'line-through', opacity: 0.5} : {})}}>{item.title}</p>
                {item.notes && <p style={s.muted}>{item.notes}</p>}
                <input
                  style={s.noteInput}
                  placeholder={checked[i] ? 'Add note (optional)' : 'Not OK? Say what is wrong - it becomes a snag in Defects'}
                  value={stepNotes[i] || ''}
                  onChange={e => setStepNotes(prev => ({...prev, [i]: e.target.value}))}
                />
                {!checked[i] && (stepNotes[i] || '').trim() && <p style={{...s.muted, color:'#8a5c0a', margin:'4px 0 0'}}>Not OK - goes to the Defects list as a snag when you complete the run</p>}
              </div>
            </div>
            )
          ))}
        </div>
        <div style={{marginTop: 24}}>
          <label style={s.label}>General notes</label>
          <textarea
            style={{...s.input, minHeight: 80, resize: 'vertical'}}
            placeholder="Any overall notes for this run..."
            value={generalNote}
            onChange={e => setGeneralNote(e.target.value)}
          />
        </div>
        <div style={{marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap'}}>
          <button style={{...s.cancelBtn, opacity: exporting ? 0.7 : 1}} onClick={signExportComplete} disabled={exporting || completing}>
            {exporting ? 'Exporting…' : 'Sign & Export PDF'}
          </button>
          <button style={{...s.saveBtn, opacity: completing ? 0.7 : 1}} onClick={() => completeRun()} disabled={completing}>
            {completing ? 'Saving...' : allDone ? 'Complete' : 'Complete (' + doneCount + '/' + items.length + ')'}
          </button>
        </div>
        <div id="chk-print" style={printMode ? {position:'absolute', left:0, top:0, width:960, zIndex:99999, background:'#ffffff'} : {position:'absolute', left:-99999, top:0, width:960, height:0, overflow:'hidden', background:'#ffffff'}}>
          {(() => {
            const NAVY='#15294a', NAVY2='#274a72', BLUE='#2457d6', INK='#26313c', MUTE='#5b6878', LINE='#e4e9f0', SOFT='#f6f9fc'
            const FONT="'Trebuchet MS','Segoe UI','Helvetica Neue',Arial,sans-serif"
            const infoCell = (label, val) => (
              <td style={{border:'1px solid '+LINE, padding:'11px 14px', verticalAlign:'top', whiteSpace:'normal', wordBreak:'break-word'}}>
                <div style={{fontSize:9, fontWeight:700, letterSpacing:'.07em', color:MUTE, textTransform:'uppercase', marginBottom:4}}>{label}</div>
                <div style={{fontSize:13, color:INK, lineHeight:1.45}}>{val || '\u2014'}</div>
              </td>
            )
            return (
            <div style={{width:960, fontFamily:FONT, color:INK, background:'#fff'}}>
              <div style={{background:'#fff', padding:'18px 30px 14px', display:'flex', justifyContent:'space-between', alignItems:'center', borderBottom:'1px solid '+LINE}}>
                <div style={{display:'flex', alignItems:'center', gap:13}}>
                  <span style={{display:'inline-block', fontSize:34, fontWeight:900, color:BLUE, lineHeight:1, letterSpacing:'-3px'}}>{'\u2248'}</span>
                  <span style={{fontSize:22, fontWeight:800, letterSpacing:'.13em', color:NAVY}}>HEYCAPTAIN</span>
                </div>
                <div style={{fontSize:10, fontWeight:700, letterSpacing:'.17em', color:MUTE, textTransform:'uppercase'}}>Safety Management System</div>
              </div>
              <div style={{background:NAVY, padding:'22px 30px', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                <div>
                  <div style={{fontSize:24, fontWeight:700, color:'#fff', letterSpacing:'.02em'}}>CHECKLIST</div>
                  <div style={{fontSize:12.5, color:'#aec3d6', marginTop:4, letterSpacing:'.02em'}}>{activeChecklist.title}{config?.vessel_name ? ' \u00b7 ' + config.vessel_name : ''}</div>
                </div>
                <div style={{border:'1.5px solid '+BLUE, borderRadius:9, padding:'10px 18px', textAlign:'center', minWidth:92}}>
                  <div style={{fontSize:9, color:'#aec3d6', letterSpacing:'.14em', marginBottom:3}}>STATUS</div>
                  <div style={{fontSize:14, fontWeight:700, color:'#fff'}}>COMPLETED</div>
                </div>
              </div>
              <div style={{padding:'24px 30px'}}>
                <div style={{margin:'0 0 10px 0', paddingBottom:6, borderBottom:'2px solid '+NAVY}}>
                  <span style={{fontSize:16, fontWeight:700, color:NAVY}}><span style={{color:BLUE}}>1</span>&nbsp;&nbsp;Record Information</span>
                </div>
                <table style={{width:'100%', borderCollapse:'collapse', marginBottom:22}}>
                  <colgroup><col style={{width:'34%'}} /><col style={{width:'33%'}} /><col style={{width:'33%'}} /></colgroup>
                  <tbody>
                    <tr>{infoCell('Checklist', activeChecklist.title)}{infoCell('Vessel', config?.vessel_name)}{infoCell('Reference', activeChecklist.ref_number || activeChecklist.reference || '\u2014')}</tr>
                    <tr>{infoCell('Date Completed', new Date().toLocaleDateString())}{infoCell('Time', new Date().toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}))}{infoCell('Status', 'Completed')}</tr>
                  </tbody>
                </table>
                <div style={{margin:'0 0 10px 0', paddingBottom:6, borderBottom:'2px solid '+NAVY}}>
                  <span style={{fontSize:16, fontWeight:700, color:NAVY}}><span style={{color:BLUE}}>2</span>&nbsp;&nbsp;Checklist Items</span>
                </div>
                <div style={{marginBottom:8}}>
                  {items.map((item, i) => {
                    if (item.is_header) return <div key={i} style={{fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:'.03em', color:'#fff', background:NAVY, padding:'9px 14px', borderRadius:6, margin:'14px 0 8px'}}>{item.title}</div>
                    if (item.is_drill_type) return <div key={i} style={{border:'1px solid '+LINE, borderRadius:8, padding:'10px 13px', margin:'6px 0', background:SOFT}}><div style={{fontSize:11, fontWeight:700, color:MUTE, textTransform:'uppercase', letterSpacing:'.04em', marginBottom:4}}>{item.title || 'Drill type(s) covered'}</div><div style={{display:'flex', flexWrap:'wrap', gap:5}}>{(stepNotes[i] || '').split(', ').filter(Boolean).map((dt, k) => <span key={k} style={{fontSize:11, fontWeight:600, color:'#fff', background:NAVY2, padding:'3px 10px', borderRadius:12}}>{dt}</span>)}{!(stepNotes[i] || '').trim() && <span style={{fontSize:12.5, color:INK}}>—</span>}</div></div>
                    if (item.is_note) return <div key={i} style={{border:'1px solid '+LINE, borderRadius:8, padding:'10px 13px', margin:'6px 0', background:SOFT}}><div style={{fontSize:11, fontWeight:700, color:MUTE, textTransform:'uppercase', letterSpacing:'.04em', marginBottom:3}}>{item.title}</div><div style={{fontSize:12.5, color:INK, whiteSpace:'pre-wrap', lineHeight:1.5}}>{stepNotes[i] || '\u2014'}</div></div>
                    if (item.is_attendance) return <div key={i} style={{border:'1px solid '+LINE, borderRadius:8, padding:'10px 13px', margin:'6px 0', background:SOFT}}><div style={{fontSize:11, fontWeight:700, color:MUTE, textTransform:'uppercase', letterSpacing:'.04em', marginBottom:3}}>{item.title || 'Crew in Attendance'}</div><div style={{fontSize:12.5, color:INK, whiteSpace:'pre-wrap', lineHeight:1.5}}>{stepNotes[i] || '\u2014'}</div></div>
                    if (item.is_signature) return <div key={i} style={{border:'1px solid '+LINE, borderRadius:8, padding:'12px 13px', margin:'10px 0', background:'#fff'}}><div style={{fontSize:11, fontWeight:700, color:MUTE, textTransform:'uppercase', letterSpacing:'.04em', marginBottom:6}}>{item.title || 'Signature'}</div>{sigImages[i] ? <img src={sigImages[i]} style={{height:64, border:'1px solid '+LINE, borderRadius:6, background:'#fff'}} /> : <div style={{fontSize:12, color:MUTE}}>Not signed</div>}<div style={{fontSize:11, color:MUTE, marginTop:5}}>Date: {new Date().toLocaleDateString()}</div></div>
                    return <div key={i} style={{display:'flex', gap:11, alignItems:'flex-start', padding:'8px 12px', borderBottom:'1px solid '+LINE}}><span style={{flexShrink:0, width:20, height:20, borderRadius:5, background: checked[i] ? '#82ad8b' : '#eef1f5', color: checked[i] ? '#fff' : MUTE, display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, lineHeight:1}}>{checked[i] ? '\u2713' : ''}</span><span style={{fontSize:13, color:INK, lineHeight:1.5, paddingTop:1}}>{item.title}{stepNotes[i] ? <span style={{color:MUTE}}>{' \u2014 ' + stepNotes[i]}</span> : ''}</span></div>
                  })}
                </div>
                {generalNote ? <div style={{marginTop:18}}><div style={{margin:'0 0 10px 0', paddingBottom:6, borderBottom:'2px solid '+NAVY}}><span style={{fontSize:16, fontWeight:700, color:NAVY}}><span style={{color:BLUE}}>3</span>&nbsp;&nbsp;General Notes</span></div><div style={{fontSize:12.5, color:INK, whiteSpace:'pre-wrap', lineHeight:1.6, padding:'2px 2px'}}>{generalNote}</div></div> : null}
              </div>
              <div style={{borderTop:'1px solid '+LINE, padding:'12px 30px', display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:8}}>
                <span style={{fontSize:11, fontWeight:800, letterSpacing:'.1em', color:NAVY}}>HEYCAPTAIN</span>
                <span style={{fontSize:10, color:MUTE}}>Hey Captain · Superyacht Safety Management System</span>
              </div>
            </div>
            )
          })()}
        </div>
      </div>
    )
  }

  return (
    <div style={s.wrap}>
      <div className="chk-tabs">
        <button className={'chk-tab' + (tab==='checklists' ? ' active' : '')} onClick={() => setTab('checklists')}>Checklists<span className="chk-count">{checklists.filter(c => !isContingency(c)).length}</span></button>
        <button className={'chk-tab' + (tab==='contingency' ? ' active' : '')} onClick={() => setTab('contingency')} style={{ color: tab === 'contingency' ? '#C62828' : undefined }}>
          <span aria-hidden="true" style={{ color: '#C62828', marginRight: 6 }}>{'\u26a0'}</span>Contingency Plans<span className="chk-count">{checklists.filter(isContingency).length}</span>
        </button>
        <button className={'chk-tab' + (tab==='workspaces' ? ' active' : '')} onClick={() => setTab('workspaces')}>Workspaces</button>
      </div>
      {emRun && !emShow && (
        <button onClick={() => setEmShow(true)} style={{ width: '100%', minHeight: 56, margin: '0 0 12px', borderRadius: 12, border: 'none', background: '#C62828', color: '#fff', fontSize: 16, fontWeight: 800, cursor: 'pointer', textAlign: 'left', padding: '0 16px' }}>
          {'\u26a0'} Emergency run in progress: {emRun.run.title} · {fmtElapsed(elapsedMs(emRun.run))} - back to the run
        </button>
      )}
      {libOpen && createPortal(<LibraryPicker kind={CONTINGENCY_LIBRARY} loadLibrary={loadLibrary} vesselId={config?.id} onClose={() => setLibOpen(false)} onAdded={() => fetchChecklists()}
        note="Hey Captain's emergency checklists, shared by every vessel. Adopting one makes the vessel's own copy: edit it to fit the vessel; the library never changes it." />, document.body)}
      {tab === 'contingency' ? (
        <ContingencyTab plans={sortPlans(checklists.filter(isContingency))} loading={loading} canEdit={canEdit} onStart={startPlan}
          onHistory={openHistory} onEdit={openEdit} onDelete={deleteChecklist}
          onNew={() => { setCategory(CONTINGENCY_CATEGORY); setShowForm(true); setTab('checklists') }}
          records={emRecords} openRecord={openRecord} setOpenRecord={setOpenRecord} saveRecord={saveRecord}
          library={library} onOpenLibrary={() => setLibOpen(true)}
          drilled={lastDrilled(drillInfo.runs)} onDrill={c => startPlan(c, 'drill', drillTypesOf(c, drillInfo.links, checklists.filter(isContingency))[0] || null)} />
      ) : tab === 'workspaces' ? <Workspaces onSaved={() => { setTab('checklists'); fetchChecklists() }} /> : (
      <>
      {!showForm && !showAI ? (
        <>
          <style>{`
            @media (max-width: 1023px) {
              .cl-toolbar { flex-wrap: wrap !important; gap: 8px !important; }
              .cl-toolbar > .cl-spacer { display: none !important; }
              .cl-search { flex: 1 1 100% !important; }
              .cl-search input { width: 100% !important; box-sizing: border-box !important; }
              .cl-toolbar > .btn { flex: 1 1 auto !important; min-width: 100px !important; font-size: 12px !important; padding: 8px 10px !important; white-space: nowrap !important; }
            }
          `}</style>
          <div className="cl-toolbar">
            <div className="cl-search"><span>⌕</span><input placeholder="Search checklists…" value={chkSearch} onChange={e => setChkSearch(e.target.value)} /></div>
            <div className="cl-spacer" style={{flex:1}} />
            <button className="btn" onClick={openGlobalHistory}>All history</button>
            <button className="btn btn-blue" onClick={() => setShowAI(true)}>Generate with AI</button>
            <button className="btn btn-gold" onClick={() => setShowForm(true)}>+ New checklist</button>
          </div>
          {loading ? <p style={s.muted}>Loading...</p>
          : checklists.length === 0 ? (
            <div style={s.empty}>
              <p style={s.muted}>No checklists yet.</p>
              <p style={s.muted}>Create your first one manually or generate from a document using AI.</p>
            </div>
          ) : (
            <div>
              {(() => {
                const shown = checklists.filter(c => !isContingency(c)).filter(c => !chkSearch || (c.title || '').toLowerCase().includes(chkSearch.toLowerCase()) || (c.description || '').toLowerCase().includes(chkSearch.toLowerCase()))
                const groups = {}
                for (const c of shown) { const g = categorise(c); (groups[g] = groups[g] || []).push(c) }
                const order = CATEGORY_ORDER.filter(g => groups[g]?.length)
                return order.map(g => {
                  const isOpen = chkSearch ? true : !!openSections[g]
                  return (
                  <div key={g} style={{marginBottom: isOpen ? 26 : 10}}>
                    <div onClick={() => setOpenSections(o => ({ ...o, [g]: !o[g] }))}
                      style={{display:'flex', alignItems:'center', gap:10, margin:'0 0 12px', paddingBottom:6, borderBottom:'2px solid var(--navy, #15294a)', cursor:'pointer', userSelect:'none'}}>
                      <span style={{fontSize:12, color:'var(--muted)', width:12, display:'inline-block'}}>{isOpen ? '\u25be' : '\u25b8'}</span>
                      <span style={{fontSize:13, fontWeight:700, letterSpacing:'.05em', textTransform:'uppercase', color:'var(--navy, #15294a)'}}>{g}</span>
                      <span style={{fontSize:11, color:'var(--muted)'}}>{groups[g].length}</span>
                    </div>
                    {isOpen && <div className="cl-grid">
                      {groups[g].slice().sort((a,b)=>(a.title||'').localeCompare(b.title||'', undefined, {numeric:true, sensitivity:'base'})).map(c => {
                        const isAI = c.source === 'ai'
                        const initials = (c.title || '?').replace(/[^A-Za-z0-9 ]/g,'').split(' ').filter(Boolean).slice(0,2).map(w => w[0]).join('').toUpperCase() || 'CL'
                        return (
                        <div key={c.id} className="cl-card">
                          <div className="cl-top">
                            <div className={'cl-ic ' + (isAI ? 'blue' : 'gold')}>{initials}</div>
                            <div style={{minWidth:0, flex:1}}>
                              <div className="cl-title">{c.title}</div>
                            </div>
                            <span className={'pill ' + (isAI ? 'pill-b' : 'pill-x')} style={{flexShrink:0}}>{isAI ? 'AI' : 'Manual'}</span>
                          </div>
                          {c.description && <div className="cl-desc" style={{display:'-webkit-box', WebkitLineClamp:3, WebkitBoxOrient:'vertical', overflow:'hidden'}}>{cleanDesc(c.description)}</div>}
                          <div className="cl-meta"><span className="num">{c.checklist_items?.length || 0} steps</span></div>
                          <div className="cl-actions">
                            <button className="cl-run" onClick={() => openRunner(c)}>Run</button>
                            <button className="cl-ghost" onClick={() => openHistory(c)} title="History">↺</button>
                            <button className="cl-ghost" onClick={() => openEdit(c)} title="Edit">✎</button>
                            <button className="cl-ghost" onClick={() => deleteChecklist(c)} title="Delete" style={{color:'var(--red)'}}>×</button>
                          </div>
                        </div>
                      )})}
                    </div>}
                  </div>
                )})
              })()}
            </div>
          )}
        </>
      ) : showAI ? (
        <div style={s.form}>
          {!aiDraft ? (
            <>
              <h2 style={s.formTitle}>Generate checklist with AI</h2>
              {aiError && <p style={s.error}>{aiError}</p>}

              <label style={s.label}>Describe what you need</label>
              <div style={{position:'relative'}}>
                <textarea
                  style={{...s.input, minHeight: 100, resize: 'vertical', paddingRight: 40}}
                  placeholder="e.g. Create a pre-departure engine check checklist for a 50m motor yacht"
                  value={aiPrompt}
                  onChange={e => setAiPrompt(e.target.value)}
                />
                <button
                  style={{position:'absolute', right:8, top:8, background: listening ? 'rgba(224,112,112,.2)' : 'none', border: listening ? '1px solid #b4313c' : '1px solid var(--border)', borderRadius:6, padding:'4px 8px', cursor:'pointer', color: listening ? '#b4313c' : 'var(--muted)', fontSize:16}}
                  onClick={() => startListening(setAiPrompt)}
                  title="Speak your prompt"
                >
                  {listening ? 'o' : 'mic'}
                </button>
              </div>
              <p style={{...s.muted, marginTop: 8, fontSize: 12}}>The AI looks in this vessel&apos;s approved manuals first (the procedure&apos;s pages, scanned pages too - it names the manual and page), then the Hey Captain library, then the regulations library. To make every checklist of a manual at once, use &quot;Create checklist from this manual&quot; on the manual&apos;s page.</p>
              <div style={s.formActions}>
                <button style={s.cancelBtn} onClick={() => { setShowAI(false); setAiError(null); setAiDraft(null) }}>Cancel</button>
                <button style={s.saveBtn} onClick={generateWithAI} disabled={aiLoading}>
                  {aiLoading ? 'Reading the manuals...' : 'Generate'}
                </button>
              </div>
            </>
          ) : (
            <>
              <h2 style={s.formTitle}>Review and edit before saving</h2>
              {aiError && <p style={s.error}>{aiError}</p>}
              {aiDraft && aiDraft.line && <p style={{...s.muted, fontSize: 12, marginTop: 0}} data-testid="cl-sources">{aiDraft.line}.{(aiDraft.sources || []).length ? ' Saved with a link to the manual.' : ''}</p>}
              <label style={s.label}>Title</label>
              <input style={s.input} value={draftTitle} onChange={e => setDraftTitle(e.target.value)} />
              <label style={s.label}>Description</label>
              <input style={s.input} value={draftDesc} onChange={e => setDraftDesc(e.target.value)} />
              <label style={s.label}>Steps - edit, remove or add</label>
              {draftSteps.map((step, i) => (
                <div key={i} style={{...s.stepRow, background: step.type==='header' ? 'rgba(184,150,62,.08)' : step.type==='note' ? 'rgba(106,170,212,.08)' : step.type==='attendance' ? 'rgba(93,186,142,.08)' : 'transparent', borderRadius:6}}>
                  {step.type==='check' && <span style={s.stepNum}>{draftSteps.slice(0,i).filter(x=>x.type==='check').length + 1}</span>}
                  <input
                    style={{...s.input, flex: 1, margin: 0, fontWeight: step.type==='header'?700:400, color: step.type==='header'?'var(--gold2)':step.type==='note'?'#2a6a96':'var(--text)'}}
                    value={step.text}
                    onChange={e => { const ns = [...draftSteps]; ns[i] = {...ns[i], text: e.target.value}; setDraftSteps(ns) }}
                  />
                  <button title="Header" style={{...s.removeBtn, color: step.type==='header'?'var(--gold2)':'var(--muted)', fontWeight:700, minWidth:24}} onClick={() => { const ns=[...draftSteps]; ns[i]={...ns[i], type: ns[i].type==='header'?'check':'header'}; setDraftSteps(ns) }}>H</button>
                  <button title="Note" style={{...s.removeBtn, color: step.type==='note'?'#2a6a96':'var(--muted)', fontWeight:700, minWidth:24}} onClick={() => { const ns=[...draftSteps]; ns[i]={...ns[i], type: ns[i].type==='note'?'check':'note'}; setDraftSteps(ns) }}>N</button>
                  <button title="Attendance" style={{...s.removeBtn, color: step.type==='attendance'?'#137050':'var(--muted)', fontWeight:700, minWidth:24}} onClick={() => { const ns=[...draftSteps]; ns[i]={...ns[i], type: ns[i].type==='attendance'?'check':'attendance'}; setDraftSteps(ns) }}>A</button>
                  <button style={s.removeBtn} onClick={() => setDraftSteps(draftSteps.filter((_, idx) => idx !== i))}>x</button>
                </div>
              ))}
              <button style={s.addStepBtn} onClick={() => setDraftSteps([...draftSteps, { text: '', type: 'check' }])}>+ Add step</button>
              <div style={s.formActions}>
                <button style={s.cancelBtn} onClick={() => setAiDraft(null)}>Back</button>
                <button style={s.saveBtn} onClick={saveAIDraft} disabled={aiLoading}>
                  {aiLoading ? 'Saving...' : 'Save checklist'}
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        <div style={s.form}>
          <h2 style={s.formTitle}>New checklist</h2>
          {error && <p style={s.error}>{error}</p>}
          <label style={s.label}>Title</label>
          <input style={s.input} placeholder="e.g. Pre-departure engine check" value={title} onChange={e => setTitle(e.target.value)} />
          <label style={s.label}>Description (optional)</label>
          <input style={s.input} placeholder="Brief description" value={description} onChange={e => setDescription(e.target.value)} />
          <label style={s.label}>Section</label>
          <select style={s.input} value={category} onChange={e => setCategory(e.target.value)}>
            {SECTION_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <label style={s.label}>Steps</label>
          {steps.map((step, i) => (
            <div key={i} style={s.stepRow}>
              <span style={s.stepNum}>{i + 1}</span>
              <input style={{...s.input, flex: 1, margin: 0}} placeholder={'Step ' + (i+1)} value={step} onChange={e => updateStep(i, e.target.value)} />
              {steps.length > 1 && <button style={s.removeBtn} onClick={() => removeStep(i)}>x</button>}
            </div>
          ))}
          <button style={s.addStepBtn} onClick={addStep}>+ Add step</button>
          <div style={s.formActions}>
            <button style={s.cancelBtn} onClick={() => { setShowForm(false); setError(null) }}>Cancel</button>
            <button style={s.saveBtn} onClick={saveChecklist} disabled={saving}>{saving ? 'Saving...' : 'Save checklist'}</button>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  )
}

// Contingency Plans tab: the emergency checklists A-Z (red, step count, what a run records), Start = emergency mode; editing
// needs Checklists Edit. Below: the emergency records (draft Incident Reports / forms and log book entries made by finished runs)
const RED = '#C62828'
function ContingencyTab({ plans, loading, canEdit, onStart, onHistory, onEdit, onDelete, onNew, records, openRecord, setOpenRecord, saveRecord, library = [], onOpenLibrary, drilled = {}, onDrill }) {
  const [draft, setDraft] = useState(null)
  useEffect(() => { setDraft(openRecord ? { title: openRecord.title, body: openRecord.body } : null) }, [openRecord])
  if (openRecord) {
    const r = openRecord, final = r.status === 'final'
    return (
      <div style={{ maxWidth: 820 }}>
        <button style={styles.backBtn} onClick={() => setOpenRecord(null)}>Back</button>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
          <span className={'pill ' + (final ? 'pill-g' : 'pill-x')}>{final ? 'Signed' : 'Draft'}</span>
          <span style={{ fontSize: 13, color: 'var(--muted)' }}>{r.kind === 'form' ? r.form_name : r.log_book} · made {new Date(r.created_at).toLocaleString()} by {r.created_by || '-'}{final ? ' · signed by ' + r.signed_by + ' ' + new Date(r.signed_at).toLocaleString() : ''}</span>
        </div>
        <input style={{ ...styles.input, fontWeight: 700, fontSize: 16 }} value={draft ? draft.title : ''} readOnly={final || !canEdit} onChange={e => setDraft(d => ({ ...d, title: e.target.value }))} aria-label="Title" />
        <textarea style={{ ...styles.input, minHeight: 360, marginTop: 10, fontSize: 14, lineHeight: 1.5, resize: 'vertical' }} value={draft ? draft.body : ''} readOnly={final || !canEdit} onChange={e => setDraft(d => ({ ...d, body: e.target.value }))} aria-label="Text" />
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
          <button className="btn" onClick={() => { try { navigator.clipboard.writeText(draft.title + '\n\n' + draft.body) } catch (e) {} }}>Copy text</button>
          {canEdit && !final && <button className="btn btn-gold" onClick={() => saveRecord(r, { title: draft.title, body: draft.body })}>Save draft</button>}
          {canEdit && !final && <button className="btn" style={{ borderColor: RED, color: RED }} onClick={() => { if (window.confirm('Sign "' + draft.title + '" as final with your name and the time? It cannot be changed afterwards (only set back to draft).')) saveRecord(r, { title: draft.title, body: draft.body, status: 'final' }) }}>Sign as final</button>}
          {canEdit && final && <button className="btn" onClick={() => { if (window.confirm('Set it back to draft? The signature is removed.')) saveRecord(r, { status: 'draft' }) }}>Back to draft</button>}
          {!canEdit && <span style={{ fontSize: 13, color: 'var(--muted)', alignSelf: 'center' }}>Editing and signing need Checklists Edit.</span>}
        </div>
      </div>
    )
  }
  return (
    <div>
      <div role="note" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', padding: '12px 16px', borderRadius: 12, border: '1.5px solid ' + RED, marginBottom: 16 }}>
        <span aria-hidden="true" style={{ color: RED, fontSize: 22 }}>{'\u26a0'}</span>
        <span style={{ flex: '1 1 260px', fontSize: 14 }}><b>In an emergency: raise the alarm / call for help per your plan first</b>, then start the plan. Every tick is recorded with the ship's time and who ticked it; Finish makes the draft reports.</span>
        {canEdit && <button className="btn" onClick={onNew}>+ New plan</button>}
      </div>
      {loading ? <p style={styles.muted}>Loading...</p> : !plans.length ? <p style={styles.muted}>No Contingency Plans on this vessel yet{library.length ? ' - adopt them from the Hey Captain library below.' : '.'}</p> : (
        <div className="cl-grid">
          {plans.map(c => {
            const rec = recordingOf(c.description)
            return (
              <div key={c.id} className="cl-card" style={{ borderColor: 'rgba(198,40,40,.45)' }}>
                <div className="cl-top">
                  <div className="cl-ic" style={{ background: RED, color: '#fff' }} aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /><path d="M12 9v4" /><path d="M12 17h.01" /></svg>
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}><div className="cl-title">{c.title}</div></div>
                  {(c.library_id || c.source === LIBRARY_SOURCE) && <LibraryTag />}
                </div>
                <div className="cl-desc">Records: {rec.forms.length ? rec.forms.join(', ') : 'no form'} · {rec.logBooks.join(', ')}</div>
                <div className="cl-meta"><span className="num">{stepCount(c)} steps</span>
                  {drilled[c.id] ? <span style={{ marginLeft: 10, fontSize: 12, color: 'var(--muted)' }}>{lastDrilledLabel(drilled[c.id], getShipTz())}</span>
                    : <span style={{ marginLeft: 10, fontSize: 12, fontWeight: 700, color: '#8a5c0a' }} title="This plan has not been run as a drill on this vessel">{'\u26a0'} Never drilled</span>}</div>
                <div className="cl-actions">
                  <button className="cl-run" style={{ background: RED, color: '#fff', minHeight: 44 }} onClick={() => onStart(c)}>Start</button>
                  {onDrill && <button className="cl-ghost" style={{ width: 'auto', padding: '0 10px', color: '#1565C0', fontWeight: 700 }} onClick={() => onDrill(c)} title="Run this plan as a drill (attendance, debrief, records the drill)">Drill</button>}
                  <button className="cl-ghost" onClick={() => onHistory(c)} title="Run history">{'\u21ba'}</button>
                  {canEdit && <button className="cl-ghost" onClick={() => onEdit(c)} title="Edit">{'\u270e'}</button>}
                  {canEdit && <button className="cl-ghost" onClick={() => onDelete(c)} title="Delete" style={{ color: 'var(--red)' }}>{'\u00d7'}</button>}
                </div>
              </div>
            )
          })}
        </div>
      )}
      {(() => {
        // what the shared picker would offer (the same engine): the library plans this vessel has not adopted (or not finished)
        const todo = library.length ? toWrite(planAdd(CONTINGENCY_LIBRARY, library, { rows: plans.map(c => ({ id: c.id, title: c.title, category: c.category, library_id: c.library_id, items: (c.checklist_items || []).length })) })) : []
        if (!library.length) return null
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 22, padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 12 }}>
            <span style={{ flex: '1 1 260px', fontSize: 14 }}><b>Hey Captain library</b> · {todo.length ? todo.length + ' plan' + (todo.length === 1 ? '' : 's') + ' not adopted on this vessel yet' : 'every plan is adopted on this vessel'}{canEdit ? '' : ' · adopting needs Checklists Edit'}</span>
            {canEdit && todo.length > 0 && <button className="btn btn-gold" onClick={onOpenLibrary}>Adopt from Hey Captain library</button>}
          </div>
        )
      })()}
      <h3 style={{ fontSize: 15, margin: '28px 0 10px', textTransform: 'uppercase', letterSpacing: '.05em' }}>Emergency records</h3>
      {records == null ? <p style={styles.muted}>The draft Incident Reports and log book entries of finished runs are listed here (needs sql/contingency.sql).</p>
        : !records.length ? <p style={styles.muted}>None yet - finishing an emergency run makes its draft Incident Report (or the plan's forms) and log book entry.</p>
        : <div style={styles.list}>{records.map(r => (
          <button key={r.id} onClick={() => setOpenRecord(r)} style={{ ...styles.card, textAlign: 'left', cursor: 'pointer', color: 'var(--text)', fontFamily: 'inherit', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ flex: '1 1 260px', fontWeight: 600 }}>{r.title}</span>
            <span className={'pill ' + (r.status === 'final' ? 'pill-g' : 'pill-x')}>{r.status === 'final' ? 'Signed' : 'Draft'}</span>
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>{new Date(r.created_at).toLocaleDateString()}</span>
          </button>
        ))}</div>}
    </div>
  )
}

// "Crew briefed" step of a pre-job checklist (RA library): each person signs on with their name and the time (ship's time) -> "Name (HH:MM)"
function BriefedStep({ item, note, setNote, crewList, extra, setExtra }) {
  const onboard = crewList.filter(cw => { const ob = getVal(cw['Onboard']); return ob === true || ob === 'true' || ob === 'Yes' || ob === 1 })
    .map(cw => (getVal(cw['First Name']) + ' ' + getVal(cw['Last Name'])).trim()).filter(Boolean)
  const signed = parseBriefed(note)
  const when = () => shipNowAuto().hm
  const guests = signed.filter(x => !onboard.some(n => n.toLowerCase() === x.name.toLowerCase()))
  const btn = { fontSize: 12, padding: '8px 14px', minHeight: 40, borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }
  return (
    <div style={{ ...styles.stepCard, flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <p style={{ ...styles.stepTitle, margin: 0, fontWeight: 600 }}>{item.title}</p>
        {onboard.length > 0 && <button type="button" style={{ ...btn, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)' }}
          onClick={() => { const t = when(); setNote(onboard.reduce((n, nm) => addBriefed(n, nm, t), note)) }}>Brief all onboard</button>}
      </div>
      {crewList.length === 0 ? <p style={styles.muted}>Loading crew…</p> : onboard.map(nm => {
        const e = signed.find(x => x.name.toLowerCase() === nm.toLowerCase())
        return (
          <div key={nm} style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between', fontSize: 13 }}>
            <span>{nm}{e && <span style={{ marginLeft: 8, color: 'var(--green)', fontWeight: 600 }}>{'\u2713'} briefed {e.time}</span>}</span>
            {e ? <button type="button" style={{ ...btn, border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted)' }} onClick={() => setNote(removeBriefed(note, nm))} aria-label={'Remove ' + nm + ' sign-on'}>Undo</button>
              : <button type="button" style={{ ...btn, border: '1px solid #2E5BFF', background: '#2E5BFF', color: '#fff' }} onClick={() => setNote(addBriefed(note, nm, when()))}>Sign on</button>}
          </div>
        )
      })}
      {guests.map(g => (
        <div key={'g-' + g.name} style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between', fontSize: 13 }}>
          <span>{g.name} <span style={{ fontSize: 11, color: 'var(--muted)' }}>(guest)</span><span style={{ marginLeft: 8, color: 'var(--green)', fontWeight: 600 }}>{'\u2713'} briefed {g.time}</span></span>
          <button type="button" style={{ ...btn, border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted)' }} onClick={() => setNote(removeBriefed(note, g.name))}>Undo</button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 6 }}>
        <input placeholder="Add other person (guest, contractor)…" value={extra} onChange={e => setExtra(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && extra.trim()) { setNote(addBriefed(note, extra, when())); setExtra('') } }}
          style={{ flex: 1, minHeight: 40, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--hull)', color: 'var(--text)', fontFamily: 'inherit' }} />
        <button type="button" style={{ ...btn, border: '1px solid #2E5BFF', background: 'transparent', color: '#2E5BFF' }} onClick={() => { if (extra.trim()) { setNote(addBriefed(note, extra, when())); setExtra('') } }}>Sign on</button>
      </div>
    </div>
  )
}

const styles = {
  wrap: { padding: '4px 0 40px', maxWidth: 1220, margin: '0 auto' },
  newBtn: { background: 'var(--gold)', color: 'var(--navy)', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  backBtn: { background: 'none', border: 'none', color: 'var(--muted)', fontSize: 14, cursor: 'pointer', marginBottom: '1rem', padding: 0 },
  muted: { color: 'var(--muted)', fontSize: 14, margin: 0 },
  empty: { marginTop: '2rem' },
  list: { display: 'flex', flexDirection: 'column', gap: 12 },
  card: { background: 'var(--ink2)', border: '1px solid var(--border)', borderRadius: 10, padding: '1rem 1.25rem' },
  cardTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 },
  cardTitle: { fontWeight: 600, fontSize: 15, color: 'var(--text)' },
  badge: { fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20 },
  cardDesc: { fontSize: 13, color: 'var(--muted)', margin: '4px 0' },
  cardMeta: { fontSize: 12, color: 'var(--muted)', marginTop: 6 },
  runBtn: { background: 'var(--gold)', color: 'var(--navy)', border: 'none', borderRadius: 6, padding: '6px 16px', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  histBtn: { background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 16px', color: 'var(--muted)', fontSize: 13, cursor: 'pointer' },
  runnerHeader: { marginBottom: 20 },
  runnerTitle: { fontSize: '1.2rem', fontWeight: 600, color: 'var(--text)', marginBottom: 4 },
  progressBar: { height: 6, background: 'var(--border)', borderRadius: 3, marginTop: 12, overflow: 'hidden' },
  progressFill: { height: '100%', background: 'var(--gold)', borderRadius: 3, transition: 'width 0.3s' },
  progressText: { fontSize: 12, color: 'var(--muted)', marginTop: 6 },
  stepsList: { display: 'flex', flexDirection: 'column', gap: 10 },
  stepCard: { display: 'flex', gap: 12, background: 'var(--ink2)', border: '1px solid var(--border)', borderRadius: 10, padding: '1rem' },
  stepDone: { borderColor: 'rgba(93,186,142,.3)', background: 'rgba(93,186,142,.05)' },
  checkbox: { width: 28, height: 28, borderRadius: '50%', border: '2px solid var(--border)', background: 'none', cursor: 'pointer', color: '#137050', fontWeight: 700, fontSize: 14, flexShrink: 0 },
  checkboxDone: { background: 'rgba(93,186,142,.2)', borderColor: '#137050' },
  stepTitle: { fontSize: 14, color: 'var(--text)', margin: '0 0 6px 0', fontWeight: 500 },
  noteInput: { width: '100%', background: 'transparent', border: 'none', borderBottom: '1px solid var(--border)', color: 'var(--muted)', fontSize: 12, padding: '4px 0', outline: 'none', boxSizing: 'border-box' },
  form: { background: 'var(--ink2)', border: '1px solid var(--border)', borderRadius: 12, padding: '1.5rem', maxWidth: 600 },
  formTitle: { fontSize: '1.1rem', fontWeight: 600, marginBottom: '1.25rem', color: 'var(--text)' },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--muted)', marginBottom: 6, marginTop: 14, textTransform: 'uppercase', letterSpacing: '0.05em' },
  input: { width: '100%', background: 'var(--ink)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', color: 'var(--text)', fontSize: 14, boxSizing: 'border-box' },
  stepRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  stepNum: { width: 24, height: 24, borderRadius: '50%', background: 'var(--gold)', color: 'var(--navy)', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  removeBtn: { background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 14, padding: '4px 8px' },
  addStepBtn: { background: 'none', border: '1px dashed var(--border)', borderRadius: 8, color: 'var(--muted)', fontSize: 13, padding: '8px 16px', cursor: 'pointer', marginTop: 4, width: '100%' },
  addTypeBtn: { background: 'none', border: '1px dashed var(--border)', borderRadius: 8, color: 'var(--muted)', fontSize: 12.5, padding: '7px 12px', cursor: 'pointer', flex: 1, minWidth: 88 },
  formActions: { display: 'flex', gap: 12, marginTop: '1.5rem', justifyContent: 'flex-end' },
  cancelBtn: { background: 'none', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--muted)', padding: '10px 20px', cursor: 'pointer', fontSize: 14 },
  saveBtn: { background: 'var(--gold)', color: 'var(--navy)', border: 'none', borderRadius: 8, padding: '10px 24px', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  expander: { display:'flex', alignItems:'center', gap:7, margin:'2px 0 14px 6px', background:'none', border:'none', color:'var(--muted)', fontSize:12, cursor:'pointer', padding:'4px 0' },
  continueBtn: { marginTop: 12, background: 'transparent', color: 'var(--gold2)', border: '0.5px solid var(--border)', borderRadius: 8, padding: '7px 16px', fontWeight: 500, fontSize: 12.5, cursor: 'pointer' },
  error: { color: '#b4313c', fontSize: 13, marginBottom: 12 },
}

// the risk assessments whose pre-job checklist this is (risk_assessments.checklist_id) - the RA <-> checklist <-> manual links are
// shown on all three (fixes-2 part 2); opens the RA. Nothing when there is none or the login cannot view Risk Assessments
function LinkedRas({ checklistId, clientId }) {
  const [ras, setRas] = useState([])
  useEffect(() => {
    if (!checklistId || !clientId) return
    let live = true
    supabase.from('risk_assessments').select('id,ra_number,activity').eq('client_id', clientId).eq('checklist_id', checklistId)
      .then(({ data }) => { if (live) setRas(data || []) }, () => {})
    return () => { live = false }
  }, [checklistId, clientId])
  if (!ras.length) return null
  const open = id => { try { localStorage.setItem('hc_open_ra', String(id)) } catch (e) {} window.dispatchEvent(new CustomEvent('hc:navigate', { detail: { page: 'risk_assessments' } })) }
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px', margin: '10px 0', fontSize: 13, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }} data-testid="cl-linked-ras">
      <b style={{ fontSize: 11, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--muted)' }}>Risk assessment{ras.length > 1 ? 's' : ''}</b>
      {ras.map(r => <button key={r.id} type="button" className="btn" style={{ padding: '2px 9px', fontSize: 12.5 }} onClick={() => open(r.id)}>{[r.ra_number, r.activity].filter(Boolean).join(' ')}</button>)}
    </div>
  )
}
